import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import axios from 'axios';
import { statusClass, statusLabel } from '../utils/statusBadge';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { ButtonGroup } from '../components/ui/button-group';
import { Input } from '../components/ui/input';
import { NumberInput } from '../components/ui/number-input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import VehicleCreateDialog from '../components/VehicleCreateDialog';
import VehicleCopyButton from '../components/VehicleCopyButton';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Toggle } from '../components/ui/toggle';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '../components/ui/tooltip';
import { Tip } from '../components/ui/tip';
import { ScrollArea } from '../components/ui/scroll-area';
import { cn } from '../lib/utils';
import { getAppSocket } from '../lib/wesSocket';
import { comboDesdeProducto, toggleComboItem } from '../lib/comboItems';
import CouponInput from '../components/CouponInput';
import { BookmarkIcon, ChevronDownIcon as ChevronDownLucide, CalendarIcon as CalendarIconLucide } from 'lucide-react';
import { es } from 'date-fns/locale';
import {
  CurrencyDollarIcon,
  PlusIcon,
  TrashIcon,
  EyeIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  DocumentTextIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ChatBubbleLeftRightIcon,
  ChartBarIcon,
  CalendarIcon,
  EnvelopeIcon,
  LinkIcon,
  DocumentArrowDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  PrinterIcon,
  ArrowPathIcon,
  PhoneArrowUpRightIcon,
  DocumentDuplicateIcon
} from '@heroicons/react/24/outline';

import BudgetPrintView from '../components/BudgetPrintView';
import ProductCombobox from '../components/ProductCombobox';
import ClientCombobox from '../components/ClientCombobox';
import ClientQuickPicks from '../components/ClientQuickPicks';
import { useTheme } from '../contexts/ThemeContext';
import { openPdf } from '../utils/pdfHelpers';
import { confirmDialog } from '../components/ConfirmDialog';
import { hoyLocal, toYMD } from '../utils/fechaLocal';
import {
  serviciosElegibles, serviciosExcluidos, entraEnDescuentoDeServicios,
  renglonConDescuento, separarPorDecision, esItemLibre, decisionManual, buscarEnCatalogo
} from '../utils/discountMatch';
import { DatePicker as DatePickerUI } from '../components/ui/date-picker';

interface Budget {
 id: string;
 budgetNumber?: string;
 clientId: string;
 clientName: string;
 vehicleId?: string;
 vehicleInfo?: string;
 mode?: string;
 selectedOption?: string;
 paymentMethod?: string;
 description: string;
 totalAmount: number;
 status: 'pending' | 'approved' | 'rejected' | 'expired' | 'completed' | 'superseded';
 // Versiones: P2026-000255 → P2026-000255-v2; la anterior queda 'superseded'
 version?: number;
 versionDe?: string | null;
 reemplazadoPor?: string | null;
 reemplazadoAt?: string | null;
 versionesAnteriores?: VersionAnterior[];
 validUntil: string;
 createdAt: string;
 updatedAt: string;
 emailSent?: boolean;
 whatsappSent?: boolean;
 items?: any[]; // Added items to Budget interface
 customDiscountId?: string; // ID del descuento personalizado aplicado
 appliedDiscount?: any; // Datos del descuento aplicado
 itemTypes?: string[];
 hasProduct?: boolean;
 hasService?: boolean;
 rejectionReason?: string | null;
 rejectionDetails?: string | null;
 rejectedAt?: string | null;
}

interface VersionAnterior {
 id: string;
 budgetNumber?: string;
 version: number;
 total: number;
 createdAt?: string | null;
 reemplazadoAt?: string | null;
}

// Se puede sacar una versión nueva de un pendiente, vencido o rechazado (no de un aprobado)
const esVersionable = (b?: { status?: string } | null) =>
  !!b && ['pending', 'expired', 'rejected'].includes(String(b.status));

// "P2026-000255-v2" se muestra como "P2026-000255" + la marca "v2"
const numeroSinVersion = (b: { budgetNumber?: string; id: string }) =>
  String(b.budgetNumber || `P-${b.id.slice(-6)}`).replace(/-v\d+$/i, '');

// Una versión vieja, con lo justo para abrirla (Ver) o bajar su PDF
const versionComoBudget = (v: VersionAnterior) => ({
  id: v.id,
  budgetNumber: v.budgetNumber,
  version: v.version,
  totalAmount: v.total,
  status: 'superseded' as const,
  versionesAnteriores: [] as VersionAnterior[]
});

const MarcaVersion = ({ version, apagada }: { version?: number; apagada?: boolean }) => (
  <span
    className={cn(
      'ml-1.5 inline-flex items-center rounded border px-1 text-[11px] font-semibold leading-4 tabular-nums align-middle',
      apagada ? 'border-border text-muted-foreground' : 'border-primary/40 bg-primary/10 text-primary'
    )}
  >
    v{version || 1}
  </span>
);

interface Client {
 id: string;
 firstName?: string;
 lastName?: string;
 companyName?: string;
 email?: string;
 phone?: string;
 type: 'individual' | 'company';
 vehicles?: Vehicle[]; // Added vehicles to Client interface
}

interface Vehicle {
 id: string;
 make?: string;
 brand?: string;
 model: string;
 year: string;
 licensePlate: string;
}

interface Product {
 id: string;
 name: string;
 sellingPrice: number;
 category: string;
 isService?: boolean;
 sku?: string;
 description?: string;
 duration?: number;
}

// Componente DatePicker configurado para una semana desde hoy
const DatePicker = ({ value, onChange, id }: { value?: string; onChange: (date: string) => void; id?: string }) => {
  const [open, setOpen] = useState(false);

  // Parsear la fecha correctamente para evitar problemas de zona horaria
  const dateValue = value ? (() => {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  })() : undefined;

  // Calcular fecha mínima (hoy) y máxima (una semana desde hoy)
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const oneWeekLater = new Date();
  oneWeekLater.setDate(today.getDate() + 7);

  const handleSelect = (date: Date | undefined) => {
    if (date) {
      // Formatear la fecha correctamente en zona horaria local
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      onChange(`${year}-${month}-${day}`);
      setOpen(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          id={id}
          className={cn(
            "w-full h-auto px-4 py-3 justify-start text-left font-normal",
            !dateValue && "text-muted-foreground"
          )}
        >
          <CalendarIconLucide className="mr-2 h-4 w-4" />
          {dateValue ? dateValue.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : <span>Seleccionar fecha</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto overflow-hidden p-0" align="start">
        <Calendar
          mode="single"
          selected={dateValue}
          onSelect={handleSelect}
          disabled={(date) => {
            // Deshabilitar fechas antes de hoy y después de una semana
            const dateOnly = new Date(date);
            dateOnly.setHours(0, 0, 0, 0);
            return dateOnly < today || dateOnly > oneWeekLater;
          }}
          captionLayout="dropdown"
          fromYear={2024}
          toYear={2026}
        />
      </PopoverContent>
    </Popover>
  );
};

// Botones "Observaciones" y "Descuento" del formulario: sin nada cargado / con algo cargado
const BOTON_VACIO = 'bg-black dark:bg-white hover:bg-black/90 dark:hover:bg-white/90 text-white dark:text-black border-black dark:border-white';
// Mismo verde en los dos temas: en este proyecto las variantes dark: de Tailwind
// no aplican (el tema oscuro va por reglas propias en el CSS global).
const BOTON_CARGADO = 'bg-emerald-700 hover:bg-emerald-800 text-white border-emerald-700';

const Budgets: React.FC = () => {
  const { isDarkMode } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
const [budgets, setBudgets] = useState<Budget[]>([]);
 const [clients, setClients] = useState<Client[]>([]);
 const [loading, setLoading] = useState(true);
 const [showCreateModal, setShowCreateModal] = useState(false);
 const [showEditModal, setShowEditModal] = useState(false);
 const [selectedBudget, setSelectedBudget] = useState<Budget | null>(null);
 // Filas con las versiones anteriores desplegadas
 const [versionesAbiertas, setVersionesAbiertas] = useState<Set<string>>(new Set());
 const [creandoVersion, setCreandoVersion] = useState<string | null>(null);
 const toggleVersiones = (id: string) => setVersionesAbiertas((prev) => {
   const next = new Set(prev);
   if (next.has(id)) next.delete(id); else next.add(id);
   return next;
 });
 const [filters, setFilters] = useState({
   search: '',
   status: '',
   priority: 'all',
   pendingDays: 'all',
   expires: 'all',
   itemType: 'all',
   clientType: 'all',
   valueRange: 'all',
   dateFrom: '',
   dateTo: ''
 });
 const [products, setProducts] = useState<Product[]>([]);
 const [budgetItems, setBudgetItems] = useState<any[]>([]);
 // Reordenar items: arrastrando en escritorio, con flechas en el celular
 // (el arrastre nativo no funciona con el dedo)
 const [dragIndex, setDragIndex] = useState<number | null>(null);
 const moverItem = (desde: number, hasta: number) => {
   if (desde === hasta || hasta < 0 || hasta >= budgetItems.length) return;
   setBudgetItems((prev) => {
     const lista = [...prev];
     const [movido] = lista.splice(desde, 1);
     lista.splice(hasta, 0, movido);
     return lista;
   });
 };
  const [optionsItems, setOptionsItems] = useState<any[]>([]);
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [showObservationsModal, setShowObservationsModal] = useState(false);

 // Debug log para optionsItems cuando se abre el modal
 useEffect(() => {
 if (showOptionsModal) {
 console.log('🔍 [OPTIONS MODAL] optionsItems:', optionsItems);
 }
 }, [showOptionsModal, optionsItems]);
  const [productSearchFilter, setProductSearchFilter] = useState<string>('');
  const [optionProductId, setOptionProductId] = useState<string>('');
  const [optionQty, setOptionQty] = useState<number>(1);
  
  // Estados para paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    const query = new URLSearchParams(location.search).get('q');
    if (query && query !== filters.search) {
      setFilters((prev) => ({ ...prev, search: query }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);
 
  // Atajo de teclado para abrir el modal de nuevo presupuesto (N)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Solo activar si no estamos escribiendo en un input
      if (
        event.target instanceof HTMLInputElement ||
        event.target instanceof HTMLTextAreaElement ||
        event.target instanceof HTMLSelectElement
      ) {
        return;
      }
      if (event.key === 'n' || event.key === 'N') {
        event.preventDefault();
        setShowCreateModal(true);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Detectar retorno desde /clients/new con cliente recién creado
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const openCreate = params.get('openCreate');
    const newClientId = params.get('newClientId');
    if (openCreate === '1') {
      setShowCreateModal(true);
      if (newClientId) {
        // Esperar a que se carguen los clientes y seleccionar el nuevo
        setTimeout(() => {
          setValue('clientId', newClientId, { shouldValidate: true });
        }, 500);
      }
      // Limpiar query params para no reabrir si recargan
      window.history.replaceState({}, '', '/budgets');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debug: verificar autenticación y estado
 useEffect(() => {
 const token = localStorage.getItem('token');
 const user = localStorage.getItem('user');
 console.log('🔐 Auth state - Token:', !!token, 'User:', !!user);
 console.log('📦 Products state:', products.length);
 }, [products]);
 // Nuevo: para edición, guardar los ítems originales
 const [originalItems, setOriginalItems] = useState<any[]>([]);
 // Estado para la vista imprimible
 const [showPrintView, setShowPrintView] = useState(false);
 const [printBudget, setPrintBudget] = useState<Budget | null>(null);
 const [printClient, setPrintClient] = useState<Client | null>(null);
 const [printVehicle, setPrintVehicle] = useState<Vehicle | null>(null);
 
 // Nuevo: estado para el modal de visualización
 const [showViewModal, setShowViewModal] = useState(false);
 const [showDiscardedOptionsModal, setShowDiscardedOptionsModal] = useState(false);
 const [viewBudget, setViewBudget] = useState<Budget | null>(null);
 const [viewClient, setViewClient] = useState<Client | null>(null);
 const [viewVehicle, setViewVehicle] = useState<Vehicle | null>(null);

 // Modal aprobar (elegir opcion)
 const [showApproveModal, setShowApproveModal] = useState(false);
 const [approveTarget, setApproveTarget] = useState<Budget | null>(null);
 const [approveOptionsCodes, setApproveOptionsCodes] = useState<string[]>([]);
 const [selectedApproveOption, setSelectedApproveOption] = useState<string>('');
 const [approveItems, setApproveItems] = useState<any[]>([]);
 // Renglones de la base que el cliente sacó al aprobar (ids). No se borran:
 // se guardan rechazados para saber después qué no quiso hacer.
 const [rejectedItems, setRejectedItems] = useState<Set<string>>(new Set());
 // Modal "Crear turno asociado" (+ opción de enlazar a un turno ya abierto)
 const [modalTurnos, setModalTurnos] = useState<Array<{ id: string; code: string; plate: string; mismoVehiculo?: boolean }>>([]);
 const [modalTurnoId, setModalTurnoId] = useState<string>('');
 const [showCreateTurnoModal, setShowCreateTurnoModal] = useState(false);
 const [budgetForTurno, setBudgetForTurno] = useState<Budget | null>(null);
 useEffect(() => {
   if (!showCreateTurnoModal || !budgetForTurno?.clientId) return;
   const vehiculoDelPresupuesto = (budgetForTurno as any)?.vehicleId || null;
   axios.get('/api/reception/queue')
     .then(res => {
       const inWork = (Object.values(res.data.queue || {}) as any[]).flat();
       const lista = inWork
         .filter((it: any) => it?.client?.id === budgetForTurno.clientId)
         .map((it: any) => ({
           id: it.id,
           code: it.appointmentCode,
           plate: it.vehicle?.licensePlate || '',
           mismoVehiculo: !!vehiculoDelPresupuesto && it.vehicle?.id === vehiculoDelPresupuesto
         }));
       // El turno del MISMO auto va primero y preseleccionado: es el que casi
       // siempre corresponde cuando el diagnóstico salió de ese mismo trabajo.
       lista.sort((a: any, b: any) => Number(b.mismoVehiculo) - Number(a.mismoVehiculo));
       setModalTurnos(lista);
       setModalTurnoId(lista.find((t: any) => t.mismoVehiculo)?.id || (lista.length === 1 ? lista[0].id : ''));
     })
     .catch(() => setModalTurnos([]));
   // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [showCreateTurnoModal, budgetForTurno?.clientId]);
 // Filtro "Aprobado sin turno"
 const [filterApprovedWithoutTurno, setFilterApprovedWithoutTurno] = useState(false);

  // Modal sugerencia de turno post-aprobación
  const [showTurnoSuggestion, setShowTurnoSuggestion] = useState(false);
  const [approvedBudgetForTurno, setApprovedBudgetForTurno] = useState<Budget | null>(null);
 
 // Nuevo: estado para selección múltiple
 const [selectedBudgets, setSelectedBudgets] = useState<Set<string>>(new Set());
 const [selectAll, setSelectAll] = useState(false);
 
// Estado para metodos de pago
const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('');
const [paymentMethods, setPaymentMethods] = useState<Array<{id: string, name: string, code: string, surcharge: number, enabled: boolean}>>([]);
const [selectedPaymentMethodsForBudget, setSelectedPaymentMethodsForBudget] = useState<string[]>([]);
const [showPaymentSelectorModal, setShowPaymentSelectorModal] = useState(false);
// Plazos de cheque: cada fila = plazo ("30 dias") + % de recargo O monto fijo
const [chequePlazos, setChequePlazos] = useState<Array<{ plazo: string; pct: string; monto: string }>>([]);
const [tempPaymentSelection, setTempPaymentSelection] = useState<string[]>([]);
const [paymentMethodsSearch, setPaymentMethodsSearch] = useState('');
const [contadoGroupExpanded, setContadoGroupExpanded] = useState(false);

  const formatPaymentMethodName = (name: string) => {
    if (typeof name !== 'string') return name;
    return name.replace(/\s*\d+\s*$/, '').trim() || name.trim();
  };

  // Codigos que pertenecen al grupo "Contado"
  const contadoGroupCodes = ['efectivo', 'debito', 'qr', 'transferencia'];

  const normalizeCode = (code: string = '') =>
    code
      .toString()
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');

  const isContadoGroupComplete = (selectedCodes: string[]) => {
    const normalizedSelected = selectedCodes.map(normalizeCode);
    return contadoGroupCodes.every(code => normalizedSelected.includes(normalizeCode(code)));
  };

  const paymentSelectionLabel = () => {
    const selected = paymentMethods.filter((m) => selectedPaymentMethodsForBudget.includes(m.code));
    if (!selected.length) return 'Seleccionar medios de pago';

    // Verificar si todos los del grupo contado estan seleccionados
    const hasCompleteContado = isContadoGroupComplete(selectedPaymentMethodsForBudget);

    // Filtrar los metodos que NO son del grupo contado
    const nonContadoSelected = selected.filter((m) =>
      !contadoGroupCodes.includes(normalizeCode(m.code))
    );

    // Construir el label
    const labels: string[] = [];
    if (hasCompleteContado) {
      labels.push('Contado');
    }

    // Agregar los otros metodos
    nonContadoSelected.forEach((m) => {
      labels.push(formatPaymentMethodName(m.name));
    });

    // Si no hay label de contado, mostrar los individuales
    if (!hasCompleteContado) {
      const contadoSelected = selected.filter((m) =>
        contadoGroupCodes.includes(normalizeCode(m.code))
      );
      contadoSelected.forEach((m) => {
        if (!labels.includes(formatPaymentMethodName(m.name))) {
          labels.push(formatPaymentMethodName(m.name));
        }
      });
    }

    // Mostrar maximo 2 y el resto como +N
    if (labels.length <= 2) {
      return labels.join(', ');
    } else {
      return `${labels.slice(0, 2).join(', ')} +${labels.length - 2}`;
    }
  };

  const applyDefaultPaymentMethods = (methods: Array<{ code: string }>, previous: string[] = []) => {
    if (previous.length > 0) return previous;

    // Medios de pago por defecto: contado (efectivo, debito, QR, transferencia) + tarjetas 3p y 6p
    const defaultCodes = [
      'efectivo',
      'debito',
      'qr',
      'transferencia',
      'tc3p',
      'tc6p'
    ];
    const defaultSet = new Set(defaultCodes.map(normalizeCode));

    const selected = methods
      .filter((m) => defaultSet.has(normalizeCode(m.code)))
      .map((m) => m.code);

    return selected.length ? selected : methods.map((m) => m.code);
  };

// Estados para descuentos personalizados
const [customDiscounts, setCustomDiscounts] = useState<any[]>([]);
const [selectedDiscount, setSelectedDiscount] = useState<string>('');
const [appliedDiscount, setAppliedDiscount] = useState<any>(null);

 // Estados para cupones
 const [appliedCoupon, setAppliedCoupon] = useState<any>(null);

 const { register, handleSubmit, reset, formState: { errors }, setValue, watch, control } = useForm();
 // "Sin descripcion" es el relleno que guarda el server cuando no se cargó ninguna observación
 const observacionesCargadas = String(watch('description') || '').trim();
 const hayObservaciones = observacionesCargadas !== '' && !/^sin descripci[oó]n$/i.test(observacionesCargadas);
 const selectedClientId = watch('clientId');

 // Turnos abiertos en el taller del cliente (para enlazar el presupuesto:
 // al aprobar, los items van al turno y el remito nace al terminar el servicio)
 const [openTurnos, setOpenTurnos] = useState<Array<{ id: string; code: string; plate: string }>>([]);
 const [linkTurnoId, setLinkTurnoId] = useState<string>('');
 useEffect(() => {
   let alive = true;
   setOpenTurnos([]);
   if (!selectedClientId) { setLinkTurnoId(''); return; }
   axios.get('/api/reception/queue')
     .then(res => {
       if (!alive) return;
       const inWork = (Object.values(res.data.queue || {}) as any[]).flat();
       setOpenTurnos(inWork
         .filter((it: any) => it?.client?.id === selectedClientId)
         .map((it: any) => ({ id: it.id, code: it.appointmentCode, plate: it.vehicle?.licensePlate || '' })));
     })
     .catch(() => {});
   return () => { alive = false; };
 }, [selectedClientId]);

 const [clientVehicles, setClientVehicles] = useState<any[]>([]);
 const [vehicleModalOpen, setVehicleModalOpen] = useState(false);

 const fetchVehiclesByClient = async (clientId: string) => {
   try {
     const response = await axios.get(`/api/vehicles?clientId=${clientId}`);
     const list = Array.isArray(response.data) ? response.data : (response.data?.vehicles || []);
     setClientVehicles(list || []);
   } catch (error) {
     console.error('Error fetching vehicles:', error);
     setClientVehicles([]);
   }
 };

 useEffect(() => {
   if (selectedClientId) {
     fetchVehiclesByClient(selectedClientId);
   } else {
     setClientVehicles([]);
   }
 }, [selectedClientId]);

 useEffect(() => {
   const socket = getAppSocket();
   const handleVehiclesUpdate = (payload: any) => {
     if (!selectedClientId) return;
     if (payload?.clientId && payload.clientId !== selectedClientId) return;
     fetchVehiclesByClient(selectedClientId);
   };
   socket.on('vehicles:update', handleVehiclesUpdate);
   return () => {
     socket.off('vehicles:update', handleVehiclesUpdate);
   };
 }, [selectedClientId]);

 const handleVehicleCreated = async (created: any) => {
   await fetchVehiclesByClient(selectedClientId);
   if (created?.id) {
     setValue('vehicleId', created.id);
   }
 };

 const vehicleCreateDialog = (
   <VehicleCreateDialog
     open={vehicleModalOpen}
     onOpenChange={setVehicleModalOpen}
     clientId={selectedClientId}
     onCreated={handleVehicleCreated}
   />
 );


 // Función para obtener la fecha de validez por defecto (7 días desde hoy)
 const getDefaultValidUntil = () => {
 const date = new Date();
 date.setDate(date.getDate() + 7);
 return toYMD(date);
 };

 // Función para cargar descuentos personalizados
 const fetchCustomDiscounts = async () => {
 try {
 const response = await axios.get('/api/custom-discounts');
 setCustomDiscounts(response.data);
 } catch (error) {
 console.error('Error cargando descuentos personalizados:', error);
 }
 };

 const isDiscountSelectable = (discount: any) => {
  if (!discount.enabled) return false;
  const today = hoyLocal();
  if (discount.start_date && discount.start_date > today) return false;
  if (discount.end_date && discount.end_date < today) return false;
  if (discount.max_uses && discount.current_uses >= discount.max_uses) return false;
  const eligibleClients = Array.isArray(discount.eligible_clients) ? discount.eligible_clients : [];
  if (eligibleClients.length > 0 && (!selectedClientId || !eligibleClients.includes(selectedClientId))) {
    return false;
  }
  if (discount.min_amount) {
    const subtotal = [...budgetItems, ...optionsItems].reduce((sum, item) => sum + (item.totalPrice || 0), 0);
    if (subtotal < discount.min_amount) return false;
  }
  return true;
 };

 const fetchClients = async () => {
 try {
 const response = await axios.get('/api/clients?limit=10000');
 setClients(response.data.clients);
 } catch (error) {
 toast.error('Error al cargar los clientes');
 }
 };

 // Crear cliente desde el buscador del presupuesto.
 // Si lo tipeado es un teléfono, crea un cliente "sin datos" al toque (con ese
 // teléfono) y lo selecciona, así se puede mandar el presupuesto por WhatsApp
 // más tarde. Si es un nombre, abre la ficha completa como antes.
 const handleCreateClientFromQuery = async (query: string) => {
   const q = (query || '').trim();
   if (!q) return;
   const digits = q.replace(/\D/g, '');
   const isPhone = /^[\d\s()+.\-]+$/.test(q) && digits.length >= 7 && digits.length <= 15;
   if (!isPhone) {
     const params = new URLSearchParams({ returnTo: '/budgets?openCreate=1', name: q });
     navigate(`/clients/new?${params.toString()}`);
     return;
   }
   try {
     const res = await axios.post('/api/clients', {
       type: 'individual',
       firstName: '',
       lastName: '',
       phone: q,
     });
     const nuevo = res.data?.client;
     if (!nuevo?.id) throw new Error('sin id');
     setClients((prev) => [nuevo, ...prev]);
     setValue('clientId', nuevo.id, { shouldValidate: true });
     setValue('vehicleId', '');
     toast.success(`Cliente sin datos creado (tel ${q}). Completá el nombre cuando puedas.`);
   } catch (error: any) {
     toast.error(error.response?.data?.error || 'No se pudo crear el cliente');
   }
 };

 const fetchProducts = async () => {
 try {
 console.log('🔄 Fetching products...');
 
 // Verificar token
 const token = localStorage.getItem('token');
 console.log('🔑 Token exists:', !!token);
 
 const response = await axios.get('/api/products?limit=all');
 console.log('📦 Products response:', response.data);
 
 const mapped = response.data.products.map((p: any) => ({
 id: p.id,
 name: p.name,
 sellingPrice: p.salePrice || 0,
 category: p.category,
 isService: p.isService || p.category === 'servicio',
 sku: p.sku,
 description: p.description,
 duration: p.duration,
 // Las normas del producto: sirven para buscarlo por lo que pide el auto
 tags: Array.isArray(p.tags) ? p.tags : [],
 // Combos ("Detailing T2"): sin esto comboDesdeProducto no encontraba las
 // inclusiones y el presupuesto salía sin ellas (y el PDF sin la lista)
 isBundle: Boolean(p.isBundle),
 bundleItems: Array.isArray(p.bundleItems) ? p.bundleItems : []
 }));
 
 console.log('🗂️ Mapped products:', mapped);
 setProducts(mapped);
 console.log('✅ Products state updated, count:', mapped.length);
 } catch (error: any) {
 console.error('❌ Error fetching products:', error);
 if (error.response) {
 console.error('📊 Response status:', error.response.status);
 console.error('📊 Response data:', error.response.data);
 }
 toast.error('Error al cargar productos y servicios');
 }
 };

 const fetchPaymentMethods = async () => {
   try {
     const response = await axios.get('/api/settings/payment-methods');
     const methods = Array.isArray(response.data) ? response.data : [];
     setPaymentMethods(methods);
     // Seleccionar todos los metodos habilitados por defecto
    const defaults = applyDefaultPaymentMethods(methods, selectedPaymentMethodsForBudget);
    setSelectedPaymentMethodsForBudget(defaults);
   } catch (error) {
     console.error('Error al cargar metodos de pago:', error);
     toast.error('Error al cargar metodos de pago');
   }
 };

  const fetchBudgets = useCallback(async (options?: { silent?: boolean }) => {
    const silent = options?.silent === true;
    if (!silent) setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.status) params.append('status', filters.status);
      const response = await axios.get(`/api/budgets?${params}`);
     // Mapear total a totalAmount para compatibilidad con el frontend
     const mappedBudgets = response.data.budgets.map((budget: any) => ({
       ...budget,
       totalAmount: budget.totalAmount || budget.total || 0
     }));
      setBudgets(mappedBudgets);
    } catch (error) {
      toast.error('Error al cargar los presupuestos');
    } finally {
      if (!silent) setLoading(false);
    }
   }, [filters.search, filters.status]);

   const realtimeTimerRef = useRef<number | null>(null);
   useEffect(() => {
    const socket = getAppSocket();

    const scheduleSilentRefresh = (payload?: any) => {
      const path = String(payload?.path || '');
      if (
        path &&
        !(
          path.startsWith('/api/budgets') ||
          path.startsWith('/api/remitos') ||
          path.startsWith('/api/clients') ||
          path.startsWith('/api/products') ||
          path.startsWith('/api/settings/payment-methods')
        )
      ) {
        return;
      }

      if (realtimeTimerRef.current) window.clearTimeout(realtimeTimerRef.current);
      realtimeTimerRef.current = window.setTimeout(() => {
        fetchBudgets({ silent: true });
      }, 450);
    };

    socket.on('area:update', scheduleSilentRefresh);
    socket.on('cash:update', scheduleSilentRefresh);
    socket.on('connect', scheduleSilentRefresh);

    return () => {
      if (realtimeTimerRef.current) window.clearTimeout(realtimeTimerRef.current);
      socket.off('area:update', scheduleSilentRefresh);
      socket.off('cash:update', scheduleSilentRefresh);
      socket.off('connect', scheduleSilentRefresh);
    };
  }, [fetchBudgets]);

  // Resetear pagina cuando cambian los filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [
    filters.search,
    filters.status,
    filters.priority,
    filters.pendingDays,
    filters.expires,
    filters.itemType,
    filters.clientType,
    filters.valueRange
  ]);

  useEffect(() => {
 console.log('🚀 Budgets component mounted, fetching data...');
 fetchBudgets();
 fetchClients();
 fetchProducts();
 fetchPaymentMethods();
 }, [fetchBudgets]);

 // Cargar datos desde diagnóstico si viene de allí
 // Estado para guardar el relatedDiagnosisId
 const [relatedDiagnosisIdFromStorage, setRelatedDiagnosisIdFromStorage] = React.useState<string | null>(null);

 useEffect(() => {
 const budgetFromDiagnosis = sessionStorage.getItem('budgetFromDiagnosis');
 if (budgetFromDiagnosis && clients.length > 0 && products.length > 0) {
 try {
 const data = JSON.parse(budgetFromDiagnosis);
 console.log('📋 Presupuesto desde diagnóstico:', data);

 // Guardar relatedDiagnosisId antes de limpiar sessionStorage
 if (data.relatedDiagnosisId || data.diagnosisId) {
 setRelatedDiagnosisIdFromStorage(data.relatedDiagnosisId || data.diagnosisId);
 }

 // Esperar un momento para que el formulario esté listo
 setTimeout(() => {
 // Pre-llenar formulario
 setValue('clientId', data.clientId);
 if (data.vehicleId) {
 setValue('vehicleId', data.vehicleId);
 }
 setValue('description', `Reparaciones según diagnóstico ${data.diagnosisNumber}`);

 // Si el diagnóstico vino de una OT abierta, preseleccionar el enlace al turno
 if (data.appointmentId) {
 setLinkTurnoId(data.appointmentId);
 }

 // Sin matching por nombre: solo el servicio ENLAZADO en el template del
 // checklist (item.linkedProductId). Los puntos sin enlace van libres en $0.
 const findMatchingProduct = (item: any) =>
 item.linkedProductId ? products.find(product => product.id === item.linkedProductId) : undefined;

 // Crear items sugeridos desde el diagnóstico con matching inteligente
 if (data.items && Array.isArray(data.items) && data.items.length > 0) {
 const suggestedItems = data.items.map((item: any) => {
 const matchedProduct = findMatchingProduct(item);

 console.log(`🔍 Buscando producto para: ${item.category} - ${item.subcategory}`);
 console.log(`✅ Producto encontrado:`, matchedProduct ? matchedProduct.name : 'ninguno');

 // Solo el ítem: el servicio enlazado o el título del punto. El comentario
 // del checklist NO pasa al presupuesto (Rodrigo, 23/10/2026): queda en el informe.
 const pointLabel = item.subcategory || item.category || 'Ítem del diagnóstico';
 const itemDescription = matchedProduct?.name || pointLabel;

 return {
 productId: matchedProduct?.id || '',
 name: itemDescription,
 description: itemDescription,
 detail: '',
 sellingPrice: matchedProduct?.sellingPrice || 0,
 unitPrice: matchedProduct?.sellingPrice || 0,
 qty: 1,
 quantity: 1,
 totalPrice: matchedProduct?.sellingPrice || 0,
 total: matchedProduct?.sellingPrice || 0
 };
 });
 setBudgetItems(suggestedItems);
 }

 // Abrir modal de creacion
 setShowCreateModal(true);

 // Limpiar sessionStorage
 sessionStorage.removeItem('budgetFromDiagnosis');

 toast.success(`Presupuesto preparado desde diagnóstico ${data.diagnosisNumber}`);
 }, 100);
 } catch (error) {
 console.error('Error cargando datos del diagnóstico:', error);
 toast.error('Error al cargar datos del diagnóstico');
 sessionStorage.removeItem('budgetFromDiagnosis');
 }
 }
 }, [setValue, clients, products]);

 // Escuchar evento de notificación para abrir modal de presupuesto
 useEffect(() => {
 const handleOpenBudgetModal = (event: CustomEvent) => {
 const { budgetId } = event.detail;
 const budget = budgets.find(b => b.id === budgetId);
 if (budget) {
 handleViewBudget(budget);
 // Refrescar la lista de presupuestos para mostrar el estado actualizado
 fetchBudgets();
 }
 };

 window.addEventListener('open-budget-modal', handleOpenBudgetModal as EventListener);
 
 return () => {
 window.removeEventListener('open-budget-modal', handleOpenBudgetModal as EventListener);
 };
 }, [budgets, fetchBudgets]);

 // Prefill first product in options modal for quick add
 useEffect(() => {
 if (showOptionsModal && !optionProductId && products.length > 0) {
 setOptionProductId(products[0].id);
 }
 }, [showOptionsModal, products, optionProductId]);

// Establecer fecha por defecto cuando se abre el modal de creacion
useEffect(() => {
  if (showCreateModal && !selectedBudget) {
    setValue('validUntil', getDefaultValidUntil());
    setSelectedPaymentMethod(''); // Limpiar metodo de pago
    const defaults = applyDefaultPaymentMethods(paymentMethods);
    setSelectedPaymentMethodsForBudget(defaults); // Activar medios de pago por defecto
    setChequePlazos([]);
    setTempPaymentSelection(defaults);
    setSelectedDiscount(''); // Limpiar descuento seleccionado
    setAppliedDiscount(null); // Limpiar descuento aplicado
    setAppliedCoupon(null); // Limpiar cupon aplicado
  }
}, [showCreateModal, selectedBudget, setValue, paymentMethods]);

// Reaplicar seleccion por defecto cuando se cargan los metodos y aun no hay seleccion
useEffect(() => {
  if (showCreateModal && !selectedBudget && paymentMethods.length > 0 && selectedPaymentMethodsForBudget.length === 0) {
    const defaults = applyDefaultPaymentMethods(paymentMethods);
    setSelectedPaymentMethodsForBudget(defaults);
    setTempPaymentSelection(defaults);
  }
}, [showCreateModal, selectedBudget, paymentMethods, selectedPaymentMethodsForBudget.length]);

 // Cargar descuentos personalizados cuando se abre el modal
 useEffect(() => {
 if (showCreateModal || showEditModal) {
 fetchCustomDiscounts();
 }
 }, [showCreateModal, showEditModal]);

 // Funciones para manejar cupones
 const handleCouponApplied = (couponData: any) => {
 setAppliedCoupon(couponData);
 };

 const handleCouponRemoved = () => {
 setAppliedCoupon(null);
 };

 const onSubmit = async (data: any) => {
 // Normalizar optionCodes: re-asignar A, B, C... según posición para evitar duplicados
 const normalizedOptions = optionsItems.map((item, idx) => ({
   ...item,
   optionCode: String.fromCharCode(65 + idx),
 }));
 // Calcular el monto total base y asegurar que cada ítem tenga totalPrice
 const merged = [...budgetItems, ...normalizedOptions];
 const processedItems = merged.map(item => {
 const qtyValue = Number(item.qty || item.quantity || 1);
 const unitPrice = Number(item.sellingPrice || item.unitPrice || 0);
 return {
 ...item,
 qty: qtyValue,
 quantity: qtyValue,
 unitPrice,                             // siempre usar el precio que editó el usuario
 sellingPrice: unitPrice,               // mantener sincronizados
 totalPrice: unitPrice * qtyValue,
 description: item.description || item.name || '',
 type: item.type || 'servicio',
 optionCode: item.optionCode || null
 };
 });
 
 const baseAmount = processedItems
 .filter((it:any) => !it.optionCode)
 .reduce((acc, item) => {
 // Si el item tiene descuento aplicado, restar el descuento del total
 if (item.discountApplied && item.discountAmount) {
 return acc + item.totalPrice - item.discountAmount;
 }
 return acc + item.totalPrice;
 }, 0);
 
 // Calcular el monto total según metodo de pago seleccionado
 const totalAmount = getSelectedPrice(baseAmount);
 
 // Si no se especifica fecha de validez, usar la fecha por defecto (una semana)
 const validUntil = data.validUntil || getDefaultValidUntil();
 
  const payload = {
    ...data,
    status: selectedBudget ? data.status : 'pending', // Nuevo presupuesto siempre es 'pending'
    validUntil,
    vehicleId: data.vehicleId ? data.vehicleId : null,
    baseAmount,
    totalAmount,
    paymentMethod: selectedPaymentMethod || 'cash',
    items: Array.isArray(processedItems) ? processedItems : [],
    paymentMethodCodes: selectedPaymentMethodsForBudget,
    paymentOptions: chequePlazos.some((pl) => pl.plazo.trim())
      ? {
          chequePlazos: chequePlazos
            .filter((pl) => pl.plazo.trim())
            .map((pl) => ({
              plazo: pl.plazo.trim(),
              pct: pl.pct === '' ? null : Number(pl.pct),
              monto: pl.monto === '' ? null : Number(pl.monto)
            }))
        }
      : undefined,
    mode: optionsItems.length > 0 ? 'options_comparative' : 'normal',
    customDiscountId: selectedDiscount || null,
    appliedDiscount: appliedDiscount,
    appliedCoupon: appliedCoupon,
    relatedDiagnosisId: relatedDiagnosisIdFromStorage
  };
 console.log('🟢 Payload enviado al backend:', payload);
 try {
 let budgetIdForLink: string | null = null;
 if (selectedBudget) {
 await axios.put(`/api/budgets/${selectedBudget.id}`, payload);
 budgetIdForLink = selectedBudget.id;
 toast.success('Presupuesto actualizado exitosamente');
 setShowEditModal(false);
 } else {
 const createdResp = await axios.post('/api/budgets', payload);
 budgetIdForLink = createdResp?.data?.id || null;
 toast.success('Presupuesto creado exitosamente');
 setShowCreateModal(false);
 // Limpiar relatedDiagnosisId después de crear el presupuesto
 setRelatedDiagnosisIdFromStorage(null);
 }
 // Enlazar a turno abierto (si se eligió): al aprobar, los items van al turno
 if (linkTurnoId && budgetIdForLink) {
   try {
     await axios.post(`/api/budgets/${budgetIdForLink}/link-appointment`, { appointmentId: linkTurnoId });
     toast.success('Presupuesto enlazado al turno');
   } catch (linkErr: any) {
     toast.error(linkErr?.response?.data?.error || 'No se pudo enlazar el turno');
   }
 }
 setLinkTurnoId('');
 clearForm();
 fetchBudgets();
 } catch (error: any) {
 toast.error(error.response?.data?.error || 'Error al procesar el presupuesto');
 }
 };

 // Función para limpiar el formulario y estados
 const clearForm = () => {
 setSelectedBudget(null);
 setBudgetItems([]);
 setOptionsItems([]);
 setOriginalItems([]);
 setSelectedPaymentMethod('');
 setSelectedPaymentMethodsForBudget(applyDefaultPaymentMethods(paymentMethods));
 setSelectedDiscount('');
 setAppliedDiscount(null);
 setAppliedCoupon(null);
 reset();
 };

 const handleEdit = async (budget: Budget) => {
 setSelectedBudget(budget);
 setValue('clientId', budget.clientId);
 setValue('vehicleId', budget.vehicleId);
 setValue('description', budget.description);
 setValue('totalAmount', budget.totalAmount);
 setValue('status', budget.status);
 setValue('validUntil', budget.validUntil.split('T')[0]);
 setValue('contactPhone', (budget as any).contactPhone || '');

 // Cargar metodo de pago si existe
 if ((budget as any).paymentMethod) {
 setSelectedPaymentMethod((budget as any).paymentMethod);
 } else {
 setSelectedPaymentMethod('cash');
 }
 
 // Cargar opciones de pago si existen
 if ((budget as any).paymentMethodCodes) {
 try {
 const codes = typeof (budget as any).paymentMethodCodes === 'string'
 ? JSON.parse((budget as any).paymentMethodCodes)
 : (budget as any).paymentMethodCodes;
 setSelectedPaymentMethodsForBudget(Array.isArray(codes) ? codes : []);
 } catch (error) {
 setSelectedPaymentMethodsForBudget(applyDefaultPaymentMethods(paymentMethods));
 }
 } else {
 setSelectedPaymentMethodsForBudget(applyDefaultPaymentMethods(paymentMethods));
 }

 // Restaurar plazos de cheque guardados
 const plazosPrevios = (budget as any).chequePlazos;
 setChequePlazos(Array.isArray(plazosPrevios)
   ? plazosPrevios.map((pl: any) => ({
       plazo: String(pl?.plazo || ''),
       pct: pl?.pct === null || pl?.pct === undefined ? '' : String(pl.pct),
       monto: pl?.monto === null || pl?.monto === undefined ? '' : String(pl.monto)
     }))
   : []);
 
 // Cargar descuento si existe
 if ((budget as any).customDiscountId) {
 setSelectedDiscount((budget as any).customDiscountId);
 if ((budget as any).appliedDiscount) {
 setAppliedDiscount((budget as any).appliedDiscount);
 }
 } else {
 setSelectedDiscount('');
 setAppliedDiscount(null);
 }
 
 // Nuevo: fetch de ítems actuales desde el backend
 try {
 const response = await axios.get(`/api/budgets/${budget.id}`);
 console.log('🔍 [EDIT] Datos del presupuesto cargados:', {
 budgetId: budget.id,
 items: response.data.items,
 itemsLength: response.data.items?.length || 0
 });
 
 if (response.data.items && Array.isArray(response.data.items)) {
 // Mapear los items para asegurar que tengan los campos correctos
 const mappedItems = response.data.items.map((item: any) => {
 // Precio base: el que quedo guardado en el item (es el que edito el usuario).
 // originalPrice solo manda cuando hay un descuento aplicado, porque ahi es el
 // precio de lista previo al descuento y el descuento se recalcula al abrir.
 // Sin esta distincion, un precio editado a mano se perdia al volver a editar.
 const basePrice = item.discountApplied
   ? (item.originalPrice || item.unitPrice || item.sellingPrice || item.price || 0)
   : (item.unitPrice || item.sellingPrice || item.originalPrice || item.price || 0);
 const quantity = item.qty || item.quantity || 1;
 const totalPrice = item.totalPrice || (basePrice * quantity);
 const itemName = item.name || item.description;
 
 // Artículo del catálogo: el productId guardado, o el nombre EXACTO sin
 // tildes/mayúsculas. Nunca el id del renglón (item.id), que no es un
 // producto, ni "contiene" (enganchaba "Bieletas de barra..." con "Bieleta").
 // Lo que no engancha es un ítem libre: sin descuento salvo la casilla.
 const delCatalogo = buscarEnCatalogo({ productId: item.productId, name: itemName }, products);
 const productId = delCatalogo ? delCatalogo.id : (item.productId || '');
 const tipoGuardado = String(item.type || 'servicio');
 const enCastellano = !['service', 'product'].includes(tipoGuardado.toLowerCase());
 const type = delCatalogo
   ? (delCatalogo.isService ? (enCastellano ? 'servicio' : 'service') : (enCastellano ? 'producto' : 'product'))
   : tipoGuardado;

 return {
 ...item,
 productId: productId,
 name: itemName,
 sellingPrice: basePrice,
 unitPrice: basePrice,
 qty: quantity,
 totalPrice: totalPrice,
 type,
 // Casilla "aplicar descuento" del renglón (la tabla la guarda 1/0/null)
 applyDiscount: decisionManual(item) ?? undefined,
 // Mantener información de descuento si existe
 originalPrice: item.originalPrice || basePrice,
 discountApplied: item.discountApplied,
 discountAmount: item.discountAmount
 };
 });
 
 console.log('🔍 [EDIT] Items mapeados:', mappedItems);
 console.log('🔍 [EDIT] Productos disponibles:', products.map(p => ({ id: p.id, name: p.name })));
 console.log('🔍 [EDIT] IDs de productos disponibles:', products.map(p => p.id));
 console.log('🔍 [EDIT] ProductIds de items:', mappedItems.map((item: any) => item.productId));
 
 // Verificar si los productIds de los items están en los productos disponibles
 mappedItems.forEach((item: any, index: number) => {
 const foundProduct = products.find(p => p.id === item.productId);
 console.log(`🔍 [MATCH CHECK] Item ${index} (${item.name}):`, {
 productId: item.productId,
 found: !!foundProduct,
 productName: foundProduct?.name || 'NO ENCONTRADO'
 });
 });
 
 // Separar items base de opciones
 const baseItems = mappedItems.filter((item: any) => !item.optionCode);
 const optionItems = mappedItems.filter((item: any) => item.optionCode);
 
 console.log('🔍 [EDIT] Items base:', baseItems);
 console.log('🔍 [EDIT] Items de opciones:', optionItems);
 console.log('🔍 [EDIT] Total items mapeados:', mappedItems.length);
 console.log('🔍 [EDIT] Items con optionCode:', mappedItems.filter((item: any) => item.optionCode).map((item: any) => ({ name: item.name, optionCode: item.optionCode })));
 
 // Descuento a servicios: recalcular cada renglón con la regla vigente
 // (los ítems libres sin descuento salvo su casilla)
 let discEdit: any = (budget as any).customDiscountId ? (budget as any).appliedDiscount : null;
 if (typeof discEdit === 'string') { try { discEdit = JSON.parse(discEdit); } catch { discEdit = null; } }
 if (discEdit && discEdit.applies_to === 'services' && discEdit.discount_type === 'percentage') {
 applyServiceDiscount(discEdit, baseItems, optionItems);
 } else {
 setBudgetItems(baseItems);
 setOptionsItems(optionItems);
 }
 setOriginalItems(mappedItems);
 } else {
 setBudgetItems([]);
 setOriginalItems([]);
 }
 } catch (error) {
 console.error('❌ [EDIT] Error cargando items:', error);
 setBudgetItems([]);
 setOriginalItems([]);
 }
 setShowEditModal(true);
 };

 const handleDelete = async (id: string) => {
 if (!await confirmDialog('¿Está seguro de eliminar este presupuesto?')) return;
 
 try {
 await axios.delete(`/api/budgets/${id}`);
 toast.success('Presupuesto eliminado exitosamente');
 await fetchBudgets();
 } catch (error: any) {
 toast.error(error.response?.data?.error || 'Error al eliminar presupuesto');
 }
 };

const handleDownloadPdf = async (budget: Budget, stopPropagation?: () => void) => {
  try {
    if (stopPropagation) stopPropagation();
    const res = await axios.post(`/api/budgets/${budget.id}/generate-pdf`, {}, { responseType: 'blob' });
    const blob = new Blob([res.data], { type: 'application/pdf' });
    const filename = budget.budgetNumber
      ? `Presupuesto-${budget.budgetNumber}.pdf`
      : `Presupuesto-${budget.id.slice(-6)}.pdf`;
    const result = openPdf({ blob, filename });
    toast.success(result === 'downloaded' ? 'PDF descargado' : 'PDF abierto');
  } catch (err: any) {
    toast.error(err.response?.data?.error || 'Error al generar PDF');
  }
};

 const handleCreateRemito = async (budgetId: string) => {
 try {
 const response = await axios.post(`/api/remitos/from-budget/${budgetId}`);
 toast.success('Remito creado desde presupuesto exitosamente');
 // Redirigir a la pagina de remitos
 window.location.href = '/remitos';
 } catch (error: any) {
 toast.error(error.response?.data?.error || 'Error al crear remito desde presupuesto');
 }
 };

 const handleConfirmBudget = async (budget: Budget) => {
 try {
 if ((budget as any).mode === 'options_comparative') {
 // Cargar items para obtener las opciones y abrir modal
 const resp = await axios.get(`/api/budgets/${budget.id}`);
 const items: any[] = Array.isArray(resp.data.items) ? resp.data.items : [];
 const codes = Array.from(new Set(items.filter(i=>i.optionCode).map(i=>String(i.optionCode))));
 if (codes.length === 0) {
 toast.error('No se encontraron opciones para este presupuesto');
 return;
 }
 // Merge con los datos completos del presupuesto (incluye appliedDiscount)
 setApproveTarget({ ...budget, ...resp.data });
 setApproveOptionsCodes(codes);
 setSelectedApproveOption(codes[0]);
 setApproveItems(items);
 // Si ya se había aprobado antes, arranca con lo que se rechazó esa vez
 setRejectedItems(new Set(items.filter(i => i.rejected).map(i => String(i.id))));
 setShowApproveModal(true);
 return;
 }

 // Presupuesto normal: mismo modal que con opciones pero sin opciones — para
 // poder destildar los renglones que el cliente no hace. El remito NO se crea
 // todavia: lo decide el modal de turno que sigue.
 {
   const resp = await axios.get(`/api/budgets/${budget.id}`);
   const items: any[] = Array.isArray(resp.data.items) ? resp.data.items : [];
   if (items.length > 0) {
     setApproveTarget({ ...budget, ...resp.data });
     setApproveOptionsCodes([]);
     setSelectedApproveOption('');
     setApproveItems(items);
     // Si ya se había aprobado antes, arranca con lo que se rechazó esa vez
     setRejectedItems(new Set(items.filter(i => i.rejected).map(i => String(i.id))));
     setShowApproveModal(true);
     return;
   }
 }

 // Sin items cargados: no hay nada que elegir, aprobar directo
 const alreadyApproved = budget.status === 'approved';
 if (!alreadyApproved) {
   await axios.put(`/api/budgets/${budget.id}`, { ...budget, status: 'approved' });
   await new Promise(r => setTimeout(r, 500));
 }
 toast.success(alreadyApproved ? 'Presupuesto ya estaba aprobado' : 'Presupuesto confirmado');
 await fetchBudgets();
 // Ya enlazado a un turno abierto: los items van al turno (lo hizo el backend
 // al aprobar) y el remito nace al terminar el servicio. No preguntar nada.
 if ((budget as any).linkedAppointmentActive) {
   toast.success(`Los ítems van al turno ${(budget as any).linkedAppointmentCode || ''}; el remito sale al terminar el servicio`);
   return;
 }
 // Abrir modal preguntando si quiere crear un turno asociado
 setBudgetForTurno(budget);
 setShowCreateTurnoModal(true);
 } catch (error: any) {
 toast.error(error.response?.data?.error || 'Error al confirmar presupuesto');
 }
 };

 // Handler para rechazar presupuesto manualmente
 const handleRejectBudget = async (budget: Budget) => {
   if (!await confirmDialog(`¿Estás seguro de rechazar el presupuesto ${budget.budgetNumber || budget.id}?`)) return;
   try {
     await axios.put(`/api/budgets/${budget.id}`, { ...budget, status: 'rejected' });
     toast.success('Presupuesto rechazado');
     await fetchBudgets();
   } catch (error: any) {
     toast.error(error.response?.data?.error || 'Error al rechazar el presupuesto');
   }
 };

 // Marcar/desmarcar que ya se recontacto al cliente de un presupuesto vencido
 // (gestion de rescate: saber a quien ya le hable para no molestarlo de nuevo)
 const handleToggleRecontactado = async (budget: Budget) => {
   try {
     const resp = await axios.post(`/api/budgets/${budget.id}/recontacted`);
     toast.success(resp.data?.recontactedAt
       ? 'Marcado como recontactado'
       : 'Marca de recontactado quitada');
     await fetchBudgets();
   } catch (error: any) {
     toast.error(error.response?.data?.error || 'No se pudo marcar el recontacto');
   }
 };

 // Handler para reabrir un presupuesto rechazado o expirado
 const handleReopenBudget = async (budget: Budget) => {
   if (!await confirmDialog(`¿Reabrir el presupuesto ${budget.budgetNumber || budget.id}? Volverá al estado "Pendiente".`)) return;
   try {
     // Extender 7 días la validez al reabrir si estaba expirado
     const newValidUntil = new Date();
     newValidUntil.setDate(newValidUntil.getDate() + 7);
     const payload: any = {
       ...budget,
       status: 'pending',
       validUntil: budget.status === 'expired'
         ? toYMD(newValidUntil)
         : (budget.validUntil || toYMD(newValidUntil))
     };
     await axios.put(`/api/budgets/${budget.id}`, payload);
     toast.success('Presupuesto reabierto');
     await fetchBudgets();
   } catch (error: any) {
     toast.error(error.response?.data?.error || 'Error al reabrir el presupuesto');
   }
 };

 // Nueva versión: copia el presupuesto (vigencia desde hoy), la anterior queda "Reemplazada"
 // y se abre el editor sobre la nueva para sacarle o cambiarle ítems
 const handleNuevaVersion = async (budget: Budget) => {
   if (creandoVersion) return;
   const siguiente = (Number(budget.version) || 1) + 1;
   const ok = await confirmDialog({
     title: `Crear la v${siguiente} de ${numeroSinVersion(budget)}`,
     description:
       'Se copian los mismos ítems con la vigencia desde hoy y se abre para editarla. ' +
       `La v${Number(budget.version) || 1} queda como "Reemplazada" y su link lleva a la nueva.`,
     confirmText: `Crear v${siguiente}`
   });
   if (!ok) return;
   setCreandoVersion(budget.id);
   try {
     const { data } = await axios.post(`/api/budgets/${budget.id}/nueva-version`);
     const nuevo = data?.budget;
     toast.success(`Versión ${nuevo?.budgetNumber || ''} creada`);
     if (showViewModal) {
       setShowViewModal(false);
       setViewBudget(null);
       setViewClient(null);
       setViewVehicle(null);
     }
     await fetchBudgets({ silent: true });
     if (nuevo?.id) await handleEdit(nuevo as Budget);
   } catch (error: any) {
     toast.error(error.response?.data?.error || 'No se pudo crear la versión nueva');
     await fetchBudgets({ silent: true });
   } finally {
     setCreandoVersion(null);
   }
 };

 const handleSendWhatsApp = async (budget: Budget) => {
 try {
 await axios.post(`/api/budgets/${budget.id}/send-whatsapp`);
 toast.success('Presupuesto enviado por WhatsApp automáticamente');
 
 // Actualizar el estado local para mostrar que se envió por WhatsApp
 setBudgets(prevBudgets => 
 prevBudgets.map(b => 
 b.id === budget.id 
 ? { ...b, whatsappSent: true }
 : b
 )
 );
 } catch (error: any) {
 toast.error(error.response?.data?.error || 'Error al enviar el presupuesto por WhatsApp');
 }
 };

 const handleSendEmail = async (budget: Budget) => {
 try {
 // Buscar el cliente para obtener su email
 const client = clients.find(c => c.id === budget.clientId);
 if (!client) {
 toast.error('Cliente no encontrado');
 return;
 }

 // Verificar si el cliente tiene email
 if (!client.email) {
 toast.error('El cliente no tiene email configurado');
 return;
 }

 // Mostrar loading
 const loadingToast = toast.loading('Enviando presupuesto por email...');
 
 // Enviar email usando el endpoint del servidor
 const response = await axios.post(`/api/budgets/${budget.id}/send-email`);
 
 // Cerrar loading y mostrar éxito
 toast.dismiss(loadingToast);
 
 if (response.data.success) {
 toast.success(`Presupuesto enviado por email a ${response.data.sentTo}`);
 
 // Actualizar el estado local inmediatamente para mostrar el icono verde
 setBudgets(prevBudgets => 
 prevBudgets.map(b => 
 b.id === budget.id 
 ? { ...b, emailSent: true }
 : b
 )
 );
 } else {
 toast.error('Error al enviar el presupuesto por email');
 }
 
 } catch (error: any) {
 console.error('Error enviando presupuesto por email:', error);
 let errorMessage = 'Error al enviar por email';
 
 if (error.response?.data?.error) {
 errorMessage = error.response.data.error;
 }
 
 toast.error(errorMessage);
 }
 };

const handlePrintView = async (budget: Budget) => {
  try {
  // Obtener datos completos del presupuesto
 const budgetResponse = await axios.get(`/api/budgets/${budget.id}`);
 const budgetData = budgetResponse.data;
 
 // Obtener cliente
 const client = clients.find(c => c.id === budget.clientId);
 if (!client) {
 toast.error('Cliente no encontrado');
 return;
 }
 
 // Obtener vehiculo si existe
 let vehicle = null;
 if (budget.vehicleId) {
 try {
 const vehicleResponse = await axios.get(`/api/vehicles/${budget.vehicleId}`);
 vehicle = vehicleResponse.data;
 } catch (vErr) {
 console.warn('Vehículo no disponible (referencia rota), continuando sin vehículo');
 }
 }

 setPrintBudget(budgetData);
 setPrintClient(client);
 setPrintVehicle(vehicle);
 setShowPrintView(true);
 } catch (error: any) {
 toast.error('Error al cargar los datos del presupuesto');
 }
};

const handleShareLink = async (budget: Budget, stopPropagation?: () => void) => {
  try {
    if (stopPropagation) stopPropagation();
    const response = await axios.post(`/api/budgets/${budget.id}/create-share-link`, { ttlDays: 7 });
    const url = response.data.url;

    // Intentar compartir nativo en moviles
    if (navigator.share) {
      await navigator.share({
        title: `Presupuesto ${budget.budgetNumber || ''}`,
        url
      });
      toast.success('Enlace interactivo compartido');
      return;
    }

    // Intentar copiar al portapapeles
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(url);
      toast.success('Enlace interactivo copiado');
      return;
    }

    // Fallback manual
    const userAction = window.prompt('Copie el enlace interactivo', url);
    if (userAction !== null) {
      toast.success('Enlace listo');
    }
  } catch (err: any) {
    toast.error(err.response?.data?.error || 'No se pudo generar el enlace interactivo');
  }
};

 const handleViewBudget = async (budget: Budget) => {
 try {
 const budgetResponse = await axios.get(`/api/budgets/${budget.id}`);
 const budgetData = budgetResponse.data;

 const client = clients.find(c => c.id === budget.clientId);
 if (!client) {
 toast.error('Cliente no encontrado');
 return;
 }

 // Vehículo: si referencia uno que ya no existe, lo ignoramos en silencio
 let vehicle = null;
 if (budget.vehicleId) {
 try {
 const vehicleResponse = await axios.get(`/api/vehicles/${budget.vehicleId}`);
 vehicle = vehicleResponse.data;
 } catch (vErr) {
 console.warn('Vehículo no disponible (referencia rota), continuando sin vehículo');
 }
 }

 setViewBudget(budgetData);
 setViewClient(client);
 setViewVehicle(vehicle);
 setShowViewModal(true);
 } catch (error: any) {
 console.error('❌ Error en handleViewBudget:', error);
 toast.error('Error al cargar los datos del presupuesto');
 }
 };

  const getStatusColor = (status: string) => statusClass('budget', status);
  const getStatusText = (status: string) => statusLabel('budget', status);

 const formatCurrency = (amount: number) => {
   const numAmount = Number(amount) || 0;
   return new Intl.NumberFormat('es-AR', {
     style: 'currency',
     currency: 'ARS'
   }).format(numAmount);
 };

 const formatDate = (dateString: string) => {
 return new Date(dateString).toLocaleDateString('es-AR');
 };

 const getClientName = (client: Client) => {
 if (client.type === 'company') return client.companyName || 'Empresa sin nombre';
 const nombre = `${client.firstName || ''} ${client.lastName || ''}`.trim();
 if (nombre) return nombre;
 return client.phone ? `Sin nombre · ${client.phone}` : 'Sin nombre';
 };

 const getVehicleInfo = (vehicle: Vehicle) => {
 return `${vehicle.make || vehicle.brand} ${vehicle.model} (${vehicle.year}) - ${vehicle.licensePlate}`;
 };

 const getBudgetAgeDays = (createdAt: string) => {
   if (!createdAt) return 0;
   const created = new Date(createdAt);
   const now = new Date();
   const diffMs = now.getTime() - created.getTime();
   return Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
 };

 const getBudgetAgeLabel = (days: number) => {
   if (days <= 0) return 'hoy';
   if (days === 1) return '1 dia';
   return `${days} dias`;
 };

 const getAgeBadgeClass = (days: number) => {
   if (days <= 2) return 'bg-green-100 text-green-800';
   if (days <= 6) return 'bg-yellow-100 text-yellow-800';
   return 'bg-red-100 text-red-800';
 };

 const getBudgetPriority = (budget: Budget) => {
   if (budget.status !== 'pending') return '-';
   const days = getBudgetAgeDays(budget.createdAt);
   if (days >= 7) return 'Alta';
   if (days >= 3) return 'Media';
   return 'Baja';
 };

 const getPriorityBadgeClass = (priority: string) => {
   if (priority === 'Alta') return 'bg-red-100 text-red-800';
   if (priority === 'Media') return 'bg-yellow-100 text-yellow-800';
   if (priority === 'Baja') return 'bg-green-100 text-green-800';
   return 'bg-gray-100 text-gray-800';
 };

 const getBudgetTypeLabel = (budget: Budget) => {
   if (budget.hasProduct && budget.hasService) return 'Mixto';
   if (budget.hasProduct) return 'Producto';
   if (budget.hasService) return 'Servicio';
   return 'Sin tipo';
 };

 const getBudgetTypeBadgeClass = (label: string) => {
   if (label === 'Producto') return 'bg-blue-100 text-blue-800';
   if (label === 'Servicio') return 'bg-purple-100 text-purple-800';
   if (label === 'Mixto') return 'bg-amber-100 text-amber-800';
   return 'bg-gray-100 text-gray-800';
 };
 
 // Función para calcular precios por metodo de pago
 const calculatePaymentPrices = (baseAmount: number) => {
 // Aplicar descuento personalizado si existe
 let discountedAmount = baseAmount;
 if (appliedDiscount) {
 if (appliedDiscount.discount_type === 'percentage') {
 const discountAmount = (baseAmount * appliedDiscount.discount_value) / 100;
 discountedAmount = baseAmount - discountAmount;
 // Aplicar descuento máximo si existe
 if (appliedDiscount.max_discount && discountAmount > appliedDiscount.max_discount) {
 discountedAmount = baseAmount - appliedDiscount.max_discount;
 }
 } else if (appliedDiscount.discount_type === 'fixed') {
 discountedAmount = baseAmount - appliedDiscount.discount_value;
 }
 }

 // Aplicar descuento del cupon si existe
 if (appliedCoupon && appliedCoupon.discount_amount) {
 discountedAmount = discountedAmount - appliedCoupon.discount_amount;
 }

 // Calcular precios dinámicamente según metodos de pago configurados
 const prices: Record<string, number> = {};
 paymentMethods.forEach(method => {
   const surchargeMultiplier = 1 + (method.surcharge / 100);
   prices[method.code] = Math.round(discountedAmount * surchargeMultiplier);
 });

 return prices;
 };
 
 // Función para obtener el precio según metodo seleccionado
 const getSelectedPrice = (baseAmount: number) => {
 if (!selectedPaymentMethod) return baseAmount;
 
 const prices = calculatePaymentPrices(baseAmount);
 return prices[selectedPaymentMethod as keyof typeof prices] || baseAmount;
 };

 // Función para aplicar descuento automático a servicios.
 // base/opciones: pasarlos cuando se acaban de cambiar (el estado todavía no
 // se actualizó), p. ej. al tildar la casilla de un ítem libre.
const applyServiceDiscount = (discount: any, base: any[] = budgetItems, opciones: any[] = optionsItems) => {
 if (discount.applies_to === 'services' && discount.discount_type === 'percentage') {
 const eligibleServices = serviciosElegibles(discount);
 const excludedServices = serviciosExcluidos(discount);
 const esServicio = (it: any) => it.type === 'service' || it.type === 'servicio';
 // Los ítems libres (sin catálogo) solo si se tildó su casilla
 const lleva = (it: any) => renglonConDescuento(it, (x) => entraEnDescuentoDeServicios(x, eligibleServices, esServicio, excludedServices));
 // Sin descuento: limpiar lo que hubiera quedado de antes (p. ej. al destildar)
 const sinDescuento = (it: any) => it.discountApplied
   ? { ...it, originalPrice: undefined, discountApplied: undefined, discountAmount: undefined }
   : it;
 // Guardar info de descuento en el renglón - NO modificar precios
 const conDescuento = (item: any) => {
 const originalPrice = item.sellingPrice || item.unitPrice || 0;
 const qty = item.qty || item.quantity || 1;
 return {
 ...item,
 // Mantener precios originales
 sellingPrice: originalPrice,
 unitPrice: originalPrice,
 totalPrice: originalPrice * qty,
 originalPrice: originalPrice, // Guardar precio original
 discountApplied: discount.name,
 discountAmount: (originalPrice * qty * discount.discount_value) / 100
 };
 };
 // Items base y de opciones: solo servicios (con lista específica, solo los de la lista)
 setBudgetItems(base.map(item => lleva(item) ? conDescuento(item) : sinDescuento(item)));
 setOptionsItems(opciones.map(item => lleva(item) ? conDescuento(item) : sinDescuento(item)));
 }
 };

 // Casilla del ítem libre: tildada = lleva el descuento; destildada = vuelve
 // a la regla (un libre no lleva descuento)
 const toggleDescuentoLibre = (index: number, aplicar: boolean) => {
 const lista = [...budgetItems];
 const { applyDiscount: _prev, ...resto } = lista[index] as any;
 lista[index] = aplicar ? { ...resto, applyDiscount: true } : resto;
 if (appliedDiscount && appliedDiscount.applies_to === 'services' && appliedDiscount.discount_type === 'percentage') {
 applyServiceDiscount(appliedDiscount, lista, optionsItems);
 } else {
 setBudgetItems(lista);
 }
 };

 // En los ítems libres (sin artículo del catálogo), la casilla para
 // aplicarles o quitarles el descuento (lo aplicado ya se ve como "-$X")
 const casillaDescuentoLibre = (item: any, index: number) => {
 if (!appliedDiscount || !esItemLibre(item)) return null;
 return (
 <label className="flex w-fit cursor-pointer items-center gap-2 text-[12px] text-muted-foreground">
 <input
 type="checkbox"
 checked={decisionManual(item) === true}
 onChange={(e) => toggleDescuentoLibre(index, e.target.checked)}
 className="h-3.5 w-3.5 accent-foreground"
 />
 <span>Aplicar descuento {appliedDiscount.name} (ítem libre)</span>
 </label>
 );
 };

 // Función para revertir descuentos aplicados a servicios
 const revertServiceDiscount = () => {
 // Revertir items base
 const revertedItems = budgetItems.map(item => {
 if (item.originalPrice) {
 return {
 ...item,
 sellingPrice: item.originalPrice,
 unitPrice: item.originalPrice,
 totalPrice: item.originalPrice * (item.qty || item.quantity || 1),
 originalPrice: undefined,
 discountApplied: undefined,
 discountAmount: undefined
 };
 }
 return item;
 });
 setBudgetItems(revertedItems);

 // Revertir items de opciones
 const revertedOptionsItems = optionsItems.map(item => {
 if (item.originalPrice) {
 return {
 ...item,
 sellingPrice: item.originalPrice,
 unitPrice: item.originalPrice,
 totalPrice: item.originalPrice * (item.qty || item.quantity || 1),
 originalPrice: undefined,
 discountApplied: undefined,
 discountAmount: undefined
 };
 }
 return item;
 });
 setOptionsItems(revertedOptionsItems);
 };

 // Función para manejar la selección de descuentos
 const handleDiscountChange = (discountId: string) => {
 setSelectedDiscount(discountId);
 if (discountId) {
 // Buscar por ID tanto como string como número para manejar ambos casos
 const discount = customDiscounts.find(d => 
 d.id === discountId || 
 d.id === parseInt(discountId) || 
 String(d.id) === discountId
 );
 setAppliedDiscount(discount);
 
 // Si es un descuento que aplica a servicios, aplicarlo automáticamente
 if (discount && discount.applies_to === 'services') {
 applyServiceDiscount(discount);
 }
 } else {
 setAppliedDiscount(null);
 // Revertir descuentos aplicados a servicios si se deselecciona
 revertServiceDiscount();
 }
 };

 // Funciones para selección múltiple
 const handleSelectBudget = (budgetId: string) => {
 const newSelected = new Set(selectedBudgets);
 if (newSelected.has(budgetId)) {
 newSelected.delete(budgetId);
 } else {
 newSelected.add(budgetId);
 }
 setSelectedBudgets(newSelected);
 setSelectAll(newSelected.size === filteredBudgets.length);
 };

const handleSelectAll = (checked: boolean) => {
  if (checked) {
    setSelectedBudgets(new Set(filteredBudgets.map(b => b.id)));
    setSelectAll(true);
  } else {
    setSelectedBudgets(new Set());
    setSelectAll(false);
  }
};

 const handleBulkAction = async (action: 'whatsapp' | 'print' | 'delete') => {
 if (selectedBudgets.size === 0) {
 toast.error('Selecciona al menos un presupuesto');
 return;
 }

 const selectedBudgetList = budgets.filter(b => selectedBudgets.has(b.id));

 switch (action) {
 case 'whatsapp':
 // Enviar WhatsApp a todos los presupuestos seleccionados
 for (const budget of selectedBudgetList) {
 await handleSendWhatsApp(budget);
 }
 toast.success(`WhatsApp enviado a ${selectedBudgetList.length} presupuesto(s)`);
 break;
 
 case 'print':
 // Imprimir todos los presupuestos seleccionados
 for (const budget of selectedBudgetList) {
 await handlePrintView(budget);
 }
 toast.success(`${selectedBudgetList.length} presupuesto(s) listo(s) para imprimir`);
 break;
 
 case 'delete':
 // Confirmar eliminación múltiple una sola vez
 if (!await confirmDialog(`¿Estás seguro de eliminar ${selectedBudgetList.length} presupuesto(s)?`)) {
 return;
 }
 
 // Eliminar directamente sin confirmación individual
 try {
 const deletePromises = selectedBudgetList.map(budget => 
 axios.delete(`/api/budgets/${budget.id}`)
 );
 
 await Promise.all(deletePromises);
 
 // Limpiar selección
 setSelectedBudgets(new Set());
 setSelectAll(false);
 
 // Recargar presupuestos
 fetchBudgets();
 
 toast.success(`${selectedBudgetList.length} presupuesto(s) eliminado(s) exitosamente`);
 } catch (error) {
 console.error('Error eliminando presupuestos:', error);
 toast.error('Error al eliminar algunos presupuestos');
 }
 break;
 }
 };

 const selectedClient = clients.find(c => c.id === selectedClientId);

 const getClientTypeById = (clientId: string) => {
   const client = clients.find((item) => item.id === clientId);
   return client?.type || 'individual';
 };

 const filteredBudgets = useMemo(() => {
   return budgets.filter((budget) => {
     if (filters.priority !== 'all') {
       const priority = getBudgetPriority(budget).toLowerCase();
       if (priority !== filters.priority) return false;
     }

     if (filters.pendingDays !== 'all') {
       const minDays = Number(filters.pendingDays);
       if (budget.status !== 'pending') return false;
       if (getBudgetAgeDays(budget.createdAt) < minDays) return false;
     }

     if (filters.expires !== 'all') {
       const validUntil = budget.validUntil ? new Date(budget.validUntil) : null;
       if (!validUntil || isNaN(validUntil.getTime())) return false;
       const today = new Date();
       today.setHours(0, 0, 0, 0);
       const start = new Date(today);
       const end = new Date(today);
       if (filters.expires === 'today') {
         if (validUntil < start || validUntil > new Date(start.getTime() + 24 * 60 * 60 * 1000 - 1)) return false;
       } else if (filters.expires === 'week') {
         end.setDate(end.getDate() + 7);
         if (validUntil < start || validUntil > end) return false;
       }
     }

     if (filters.dateFrom || filters.dateTo) {
       const dia = budget.createdAt ? toYMD(new Date(budget.createdAt)) : '';
       if (filters.dateFrom && (!dia || dia < filters.dateFrom)) return false;
       if (filters.dateTo && (!dia || dia > filters.dateTo)) return false;
     }

     if (filters.itemType !== 'all') {
       if (filters.itemType === 'product' && !budget.hasProduct) return false;
       if (filters.itemType === 'service' && !budget.hasService) return false;
     }

     if (filters.clientType !== 'all') {
       const clientType = getClientTypeById(budget.clientId);
       if (filters.clientType === 'company' && clientType !== 'company') return false;
       if (filters.clientType === 'individual' && clientType !== 'individual') return false;
     }

     if (filters.valueRange !== 'all') {
       const amount = Number(budget.totalAmount) || 0;
       if (filters.valueRange === '0-50000' && (amount < 0 || amount > 50000)) return false;
       if (filters.valueRange === '50000-200000' && (amount < 50000 || amount > 200000)) return false;
       if (filters.valueRange === '200000-500000' && (amount < 200000 || amount > 500000)) return false;
       if (filters.valueRange === '500000+' && amount < 500000) return false;
     }

     // Filtro: aprobados sin turno asignado
     if (filterApprovedWithoutTurno) {
       if (budget.status !== 'approved') return false;
       if ((budget as any).hasAppointment) return false;
     }

     return true;
   });
 }, [budgets, clients, filters, filterApprovedWithoutTurno]);

 // Calcular métricas
 const calculateMetrics = (source: Budget[]) => {
   const total = source.length;
   const pending = source.filter(b => b.status === 'pending').length;
   const approved = source.filter(b => b.status === 'approved').length;
   const rejected = source.filter(b => b.status === 'rejected').length;
   const expired = source.filter(b => b.status === 'expired').length;
   // Con opciones A/B/C el total guardado es solo la base; el backend manda
   // estimatedTotal (base + promedio de opciones) para estimar lo no aprobado
   const valueOf = (b: Budget) => Number((b as any).estimatedTotal ?? b.totalAmount) || 0;
   const totalValue = source.reduce((acc, b) => acc + valueOf(b), 0);
   const pendingValue = source.filter(b => b.status === 'pending')
     .reduce((acc, b) => acc + valueOf(b), 0);
   const pendingIsEstimate = source.some(b => b.status === 'pending' && (b as any).estimatedTotal != null);
   const approvedValue = source.filter(b => b.status === 'approved')
     .reduce((acc, b) => acc + (Number(b.totalAmount) || 0), 0);
   const lostValue = source.filter(b => b.status === 'rejected' || b.status === 'expired')
     .reduce((acc, b) => acc + valueOf(b), 0);
   const lostIsEstimate = source.some(b => (b.status === 'rejected' || b.status === 'expired') && (b as any).estimatedTotal != null);
   const approvalRate = total > 0 ? Math.round((approved / total) * 100) : 0;

   return {
     total,
     pending,
     approved,
     rejected,
     expired,
     totalValue,
     pendingValue,
     pendingIsEstimate,
     approvedValue,
     lostValue,
     lostIsEstimate,
     approvalRate
   };
 };

const metrics = calculateMetrics(filteredBudgets);
const statusTabs = [
  { value: 'all', label: 'Todos' },
  { value: 'pending', label: 'Pendientes' },
  { value: 'approved', label: 'Aprobados' },
  { value: 'rejected', label: 'Rechazados' },
  { value: 'expired', label: 'Expirados' },
  { value: 'completed', label: 'Completados' }
];
const statusValue = filters.status || 'all';

const handleStatusChange = (value: string) => {
  setFilters((prev) => ({
    ...prev,
    status: value === 'all' ? '' : value
  }));
};

  // Calcular paginación
  const totalPages = Math.ceil(filteredBudgets.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedBudgets = filteredBudgets.slice(startIndex, endIndex);
  const totalResults = filteredBudgets.length;
  const startResult = filteredBudgets.length === 0 ? 0 : startIndex + 1;
  const endResult = Math.min(endIndex, filteredBudgets.length);

  // Funciones de navegación de paginación
  const goToFirstPage = () => setCurrentPage(1);
  const goToPreviousPage = () => setCurrentPage(prev => Math.max(1, prev - 1));
  const goToNextPage = () => setCurrentPage(prev => Math.min(totalPages, prev + 1));
  const goToLastPage = () => setCurrentPage(totalPages);

  const enabledDiscounts = customDiscounts.filter(isDiscountSelectable);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Presupuestos</h2>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end">
          <button type="button"
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center justify-center whitespace-nowrap text-sm font-bold h-8 rounded-lg gap-1.5 px-3 bg-black dark:bg-white text-white dark:text-black shadow-xs hover:bg-black/90 dark:hover:bg-white/90"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Nuevo presupuesto
            <kbd className="ml-2 inline-flex items-center justify-center px-1 text-xs font-bold font-sans bg-gray-200 dark:bg-gray-800 text-gray-900 dark:text-white border-gray-300 dark:border-gray-700 rounded-sm border leading-none">
              N
            </kbd>
          </button>
        </div>
      </div>

      {/* Buscador y Filtro de Estado */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground pointer-events-none z-10" />
          <Input
            type="text"
            placeholder="Buscar por código, cliente, patente, marca o modelo..."
            value={filters.search}
            onChange={e => setFilters({ ...filters, search: e.target.value })}
            className="file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground bg-background dark:bg-input/30 border-input flex min-w-0 rounded-lg border px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive h-8 w-full pl-8"
          />
        </div>
        <Select value={statusValue} onValueChange={handleStatusChange}>
          <SelectTrigger className="w-full sm:w-[180px] h-8">
            <SelectValue placeholder="Filtrar por estado" />
          </SelectTrigger>
          <SelectContent>
            {statusTabs.map((tab) => (
              <SelectItem key={tab.value} value={tab.value}>
                {tab.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6">
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Prioridad</Label>
          <Select value={filters.priority} onValueChange={(value) => setFilters(prev => ({ ...prev, priority: value }))}>
            <SelectTrigger className="h-8">
              <SelectValue placeholder="Todas" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              <SelectItem value="alta">Alta</SelectItem>
              <SelectItem value="media">Media</SelectItem>
              <SelectItem value="baja">Baja</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Pendientes + dias</Label>
          <Select value={filters.pendingDays} onValueChange={(value) => setFilters(prev => ({ ...prev, pendingDays: value }))}>
            <SelectTrigger className="h-8">
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="3">3 dias</SelectItem>
              <SelectItem value="7">7 dias</SelectItem>
              <SelectItem value="15">15 dias</SelectItem>
              <SelectItem value="30">30 dias</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Expiran</Label>
          <Select value={filters.expires} onValueChange={(value) => setFilters(prev => ({ ...prev, expires: value }))}>
            <SelectTrigger className="h-8">
              <SelectValue placeholder="Todas" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              <SelectItem value="today">Hoy</SelectItem>
              <SelectItem value="week">Esta semana</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Tipo</Label>
          <Select value={filters.itemType} onValueChange={(value) => setFilters(prev => ({ ...prev, itemType: value }))}>
            <SelectTrigger className="h-8">
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="product">Producto</SelectItem>
              <SelectItem value="service">Servicio</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Cliente</Label>
          <Select value={filters.clientType} onValueChange={(value) => setFilters(prev => ({ ...prev, clientType: value }))}>
            <SelectTrigger className="h-8">
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="individual">Particular</SelectItem>
              <SelectItem value="company">Empresa</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Rango $</Label>
          <Select value={filters.valueRange} onValueChange={(value) => setFilters(prev => ({ ...prev, valueRange: value }))}>
            <SelectTrigger className="h-8">
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="0-50000">$0 a $50k</SelectItem>
              <SelectItem value="50000-200000">$50k a $200k</SelectItem>
              <SelectItem value="200000-500000">$200k a $500k</SelectItem>
              <SelectItem value="500000+">$500k+</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Desde</Label>
          <DatePickerUI value={filters.dateFrom} onChange={(v) => setFilters(prev => ({ ...prev, dateFrom: v }))} className="h-8 w-full" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Hasta</Label>
          <DatePickerUI value={filters.dateTo} onChange={(v) => setFilters(prev => ({ ...prev, dateTo: v }))} className="h-8 w-full" />
        </div>
      </div>

      {/* Métricas */}
      <div className="space-y-4">

        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <div className="rounded-lg border p-4">
            <p className="text-xs text-muted-foreground">Total</p>
            <p className="text-lg font-semibold">{metrics.total}</p>
          </div>
          <div className="rounded-lg border p-4">
            <p className="text-xs text-muted-foreground">Pendientes</p>
            <p className="text-lg font-semibold">{metrics.pending}</p>
          </div>
          <div className="rounded-lg border p-4">
            <p className="text-xs text-muted-foreground">Aprobados</p>
            <p className="text-lg font-semibold">{metrics.approved}</p>
          </div>
          <Tip label="Click para filtrar">
            <div
              className={`rounded-lg border p-4 cursor-pointer transition-colors ${filterApprovedWithoutTurno ? 'border-primary bg-primary/10' : 'hover:bg-accent/50'}`}
              onClick={() => setFilterApprovedWithoutTurno((v) => !v)}
            >
              <p className="text-xs text-muted-foreground">Aprobados sin turno</p>
              <p className="text-lg font-semibold">
                {budgets.filter((b) => b.status === 'approved' && !(b as any).hasAppointment).length}
              </p>
            </div>
          </Tip>
          <div className="rounded-lg border p-4">
            <p className="text-xs text-muted-foreground">Rechazados</p>
            <p className="text-lg font-semibold">{metrics.rejected}</p>
          </div>
          <Tip label="Click para filtrar">
            <div
              className={`rounded-lg border p-4 cursor-pointer transition-colors ${statusValue === 'expired' ? 'border-primary bg-primary/10' : 'hover:bg-accent/50'}`}
              onClick={() => handleStatusChange(statusValue === 'expired' ? 'all' : 'expired')}
            >
              <p className="text-xs text-muted-foreground">Expirados</p>
              <p className="text-lg font-semibold">{budgets.filter((b) => b.status === 'expired').length}</p>
            </div>
          </Tip>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Tip label={metrics.pendingIsEstimate ? 'Incluye presupuestos con opciones: se estima base + promedio de las opciones' : '$ en presupuestos pendientes'}>
            <div className="rounded-lg border p-4">
              <p className="text-xs text-muted-foreground">$ Pendiente</p>
              <p className="text-lg font-semibold">{metrics.pendingIsEstimate ? '~' : ''}{formatCurrency(metrics.pendingValue)}</p>
            </div>
          </Tip>
          <div className="rounded-lg border p-4">
            <p className="text-xs text-muted-foreground">$ Aprobado</p>
            <p className="text-lg font-semibold">{formatCurrency(metrics.approvedValue)}</p>
          </div>
          <Tip label={metrics.lostIsEstimate ? 'Incluye presupuestos con opciones: se estima base + promedio de las opciones' : '$ en presupuestos rechazados o vencidos'}>
            <div className="rounded-lg border p-4">
              <p className="text-xs text-muted-foreground">$ Perdido</p>
              <p className="text-lg font-semibold">{metrics.lostIsEstimate ? '~' : ''}{formatCurrency(metrics.lostValue)}</p>
            </div>
          </Tip>
        </div>
      </div>

      {/* Vista principal */}
      <Card className="border-0 shadow-none md:border md:shadow-sm">
        <CardContent className="p-0">
          <div className="hidden md:block relative w-full overflow-x-auto">
            <Table>
                <TableHeader>
                  <TableRow className="hover:bg-muted/50 border-b">
                    <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Codigo</TableHead>
                    <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Cliente</TableHead>
                    <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Tipo</TableHead>
                    <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Estado</TableHead>
                    <TableHead className="hidden 2xl:table-cell text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Antiguedad</TableHead>
                    <TableHead className="hidden 2xl:table-cell text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Prioridad</TableHead>
                    <TableHead className="text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Valor</TableHead>
                    <TableHead className="hidden 2xl:table-cell text-foreground h-8 px-2 align-middle font-medium whitespace-nowrap text-left">Válido hasta</TableHead>
                    <TableHead className="text-foreground h-8 px-2 text-left align-middle font-medium whitespace-nowrap"></TableHead>
                  </TableRow>
                </TableHeader>
                  <TableBody>
                    {filteredBudgets.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-12">
                          <CurrencyDollarIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                          <p className="text-muted-foreground">No hay presupuestos que coincidan con los filtros</p>
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedBudgets.map((budget) => {
                        const isSelected = selectedBudgets.has(budget.id);
                        const ageDays = getBudgetAgeDays(budget.createdAt);
                        const priority = getBudgetPriority(budget);
                        const typeLabel = getBudgetTypeLabel(budget);
                        const anteriores = budget.versionesAnteriores || [];
                        const abiertas = versionesAbiertas.has(budget.id);

                        return (
                          <React.Fragment key={budget.id}>
                          <TableRow
                            className={`border-b hover:bg-muted/50 cursor-pointer ${isSelected ? 'bg-muted' : ''}`}
                          >
                            <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-left">
                              <div className="font-medium">
                                {numeroSinVersion(budget)}
                                {(Number(budget.version) || 1) > 1 && <MarcaVersion version={budget.version} />}
                              </div>
                              {anteriores.length > 0 && (
                                <button
                                  type="button"
                                  aria-expanded={abiertas}
                                  onClick={(e) => { e.stopPropagation(); toggleVersiones(budget.id); }}
                                  className="mt-0.5 inline-flex items-center gap-0.5 rounded text-xs text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                                >
                                  {abiertas ? <ChevronDownIcon className="h-3 w-3" /> : <ChevronRightIcon className="h-3 w-3" />}
                                  {anteriores.length === 1 ? '1 versión anterior' : `${anteriores.length} versiones anteriores`}
                                </button>
                              )}
                            </TableCell>
                            <TableCell className="py-1 px-2 align-middle text-left max-w-[140px] cq-clamp-wide">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="font-medium truncate cursor-default">{budget.clientName}</div>
                                </TooltipTrigger>
                                <TooltipContent side="top">{budget.clientName}</TooltipContent>
                              </Tooltip>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="text-sm text-muted-foreground truncate cursor-default">
                                    {budget.vehicleInfo || 'Sin vehículo'}
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent side="bottom">{budget.vehicleInfo || 'Sin vehículo'}</TooltipContent>
                              </Tooltip>
                            </TableCell>
                            <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-left">
                              <Badge className={getBudgetTypeBadgeClass(typeLabel)}>{typeLabel}</Badge>
                            </TableCell>
                            <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-left">
                              <Badge 
                                variant={
                                  budget.status === 'pending' ? 'default' :
                                  budget.status === 'approved' ? 'default' :
                                  budget.status === 'rejected' ? 'destructive' :
                                  'secondary'
                                }
                                className={
                                  budget.status === 'pending' ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100' :
                                  budget.status === 'approved' ? 'bg-green-100 text-green-800 hover:bg-green-100' :
                                  budget.status === 'expired' ? 'bg-gray-100 text-gray-800 hover:bg-gray-100' :
                                  ''
                                }
                              >
                                {getStatusText(budget.status)}
                              </Badge>
                              {budget.status === 'expired' && (budget as any).recontactedAt && (
                                <Badge className="ml-1 bg-green-100 text-green-800 hover:bg-green-100">
                                  Recontactado
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="hidden 2xl:table-cell py-1 px-2 align-middle whitespace-nowrap text-left">
                              <Badge className={getAgeBadgeClass(ageDays)}>{getBudgetAgeLabel(ageDays)}</Badge>
                            </TableCell>
                            <TableCell className="hidden 2xl:table-cell py-1 px-2 align-middle whitespace-nowrap text-left">
                              {priority === '-' ? (
                                <span className="text-muted-foreground">-</span>
                              ) : (
                                <Badge className={getPriorityBadgeClass(priority)}>{priority}</Badge>
                              )}
                            </TableCell>
                            <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-left">
                              <div className="font-medium">{formatCurrency(budget.totalAmount || 0)}</div>
                            </TableCell>
                            <TableCell className="hidden 2xl:table-cell py-1 px-2 align-middle whitespace-nowrap text-left">
                              <div className="text-muted-foreground">{formatDate(budget.validUntil)}</div>
                            </TableCell>
                            <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-right" onClick={(e) => e.stopPropagation()}>
                              <div className="flex items-center justify-end">
                                <ButtonGroup>
                                  <Tip label="Ver detalles">
                                    <Button
                                      variant="outline"
                                      size="icon"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleViewBudget(budget);
                                      }}
                                      className="size-8"
                                    >
                                      <EyeIcon className="h-4 w-4" />
                                    </Button>
                                  </Tip>
                                  {esVersionable(budget) && (
                                    <Tip label={`Nueva versión (v${(Number(budget.version) || 1) + 1}): copia con vigencia desde hoy`}>
                                      <Button
                                        variant="outline"
                                        size="icon"
                                        disabled={creandoVersion === budget.id}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleNuevaVersion(budget);
                                        }}
                                        className="size-8"
                                        aria-label="Nueva versión"
                                      >
                                        <DocumentDuplicateIcon className="h-4 w-4" />
                                      </Button>
                                    </Tip>
                                  )}
                                  {/* Un vencido no se comparte: el link sobraba y ensanchaba la fila */}
                                  {budget.status !== 'expired' && (
                                    <Tip label="Crear/Copiar enlace interactivo">
                                      <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleShareLink(budget);
                                        }}
                                        className="size-8"
                                      >
                                        <LinkIcon className="h-4 w-4" />
                                      </Button>
                                    </Tip>
                                  )}
                                  <Tip label={budget.whatsappSent ? "WhatsApp enviado" : "Enviar por WhatsApp"}>
                                    <Toggle
                                      variant="outline"
                                      size="sm"
                                      pressed={budget.whatsappSent || false}
                                      onPressedChange={(pressed: boolean) => {
                                        if (budget.whatsappSent) return; // Ya enviado, no hacer nada
                                        if (pressed) {
                                          handleSendWhatsApp(budget);
                                        }
                                      }}
                                      disabled={budget.whatsappSent}
                                      className="size-8 data-[state=on]:bg-green-100 data-[state=on]:text-green-600 data-[state=on]:border-green-300"
                                      aria-label={budget.whatsappSent ? "WhatsApp enviado" : "Enviar por WhatsApp"}
                                    >
                                      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                                      </svg>
                                    </Toggle>
                                  </Tip>
                                  <Tip label={budget.emailSent ? 'Email ya enviado' : 'Enviar por email'}>
                                    <Toggle
                                      variant="outline"
                                      size="sm"
                                      pressed={budget.emailSent || false}
                                      onPressedChange={(pressed: boolean) => {
                                        if (budget.emailSent) return; // Ya enviado, no hacer nada
                                        if (pressed) {
                                          handleSendEmail(budget);
                                        }
                                      }}
                                      disabled={budget.emailSent}
                                      className="size-8 data-[state=on]:bg-green-100 data-[state=on]:text-green-600 data-[state=on]:border-green-300"
                                      aria-label={budget.emailSent ? 'Email ya enviado' : 'Enviar por email'}
                                    >
                                      <EnvelopeIcon className="h-4 w-4" />
                                    </Toggle>
                                  </Tip>
                                  {(budget.status === 'pending' || (budget.status === 'approved' && !(budget as any).hasRemito)) && (
                                    <Tip label={budget.status === 'approved' ? 'Regenerar remito' : 'Aprobar presupuesto y crear remito'}>
                                      <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleConfirmBudget(budget);
                                        }}
                                        className="size-8 text-green-600"
                                      >
                                        <CheckIcon className="h-4 w-4" />
                                      </Button>
                                    </Tip>
                                  )}
                                  {budget.status === 'pending' && (
                                    <Tip label="Rechazar presupuesto">
                                      <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleRejectBudget(budget);
                                        }}
                                        className="size-8 text-red-600 hover:text-red-700"
                                      >
                                        <XMarkIcon className="h-4 w-4" />
                                      </Button>
                                    </Tip>
                                  )}
                                  {(budget.status === 'rejected' || budget.status === 'expired') && (
                                    <Tip label="Reabrir presupuesto (vuelve a pendiente)">
                                      <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleReopenBudget(budget);
                                        }}
                                        className="size-8 text-blue-600 hover:text-blue-700"
                                      >
                                        <ArrowPathIcon className="h-4 w-4" />
                                      </Button>
                                    </Tip>
                                  )}
                                  {budget.status === 'expired' && (
                                    <Tip label={(budget as any).recontactedAt
                                      ? `Recontactado el ${new Date((budget as any).recontactedAt).toLocaleDateString('es-AR')} — click para quitar la marca`
                                      : 'Marcar recontactado: ya le hablé, no volver a molestarlo'}>
                                      <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleToggleRecontactado(budget);
                                        }}
                                        className={`size-8 ${(budget as any).recontactedAt
                                          ? 'border-green-600 bg-green-600/10 text-green-600 hover:text-green-700'
                                          : 'text-muted-foreground hover:text-foreground'}`}
                                      >
                                        <PhoneArrowUpRightIcon className="h-4 w-4" />
                                      </Button>
                                    </Tip>
                                  )}
                                  <Tip label="Descargar PDF">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDownloadPdf(budget);
                                      }}
                                      className="h-8 px-3 gap-1"
                                      aria-label="Descargar PDF"
                                    >
                                      <DocumentArrowDownIcon className="h-4 w-4 md:mr-0 mr-1" />
                                      <span className="md:hidden text-xs font-medium">PDF</span>
                                    </Button>
                                  </Tip>
                                  <Tip label="Eliminar presupuesto">
                                    <Button
                                      variant="outline"
                                      size="icon"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDelete(budget.id);
                                      }}
                                      className="size-8"
                                    >
                                      <TrashIcon className="h-4 w-4" />
                                    </Button>
                                  </Tip>
                                </ButtonGroup>
                              </div>
                            </TableCell>
                          </TableRow>
                          {abiertas && anteriores.map((v) => {
                            const comoBudget = { ...budget, ...versionComoBudget(v) };
                            return (
                              <TableRow key={v.id} className="border-b bg-muted/30 hover:bg-muted/40 text-muted-foreground">
                                <TableCell className="py-1 px-2 pl-5 align-middle whitespace-nowrap text-left">
                                  <span className="font-medium">{numeroSinVersion(comoBudget)}</span>
                                  <MarcaVersion version={v.version} apagada />
                                </TableCell>
                                <TableCell colSpan={2} className="py-1 px-2 align-middle whitespace-nowrap text-left text-sm">
                                  {v.reemplazadoAt ? `Reemplazada el ${formatDate(v.reemplazadoAt)}` : 'Reemplazada'}
                                </TableCell>
                                <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-left">
                                  <Badge className={`${getStatusColor('superseded')} hover:bg-gray-100`}>{getStatusText('superseded')}</Badge>
                                </TableCell>
                                <TableCell className="hidden 2xl:table-cell" />
                                <TableCell className="hidden 2xl:table-cell" />
                                <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-left tabular-nums">
                                  {formatCurrency(v.total)}
                                </TableCell>
                                <TableCell className="hidden 2xl:table-cell" />
                                <TableCell className="py-1 px-2 align-middle whitespace-nowrap text-right">
                                  <div className="flex items-center justify-end">
                                    <ButtonGroup>
                                      <Tip label={`Ver la v${v.version}`}>
                                        <Button variant="outline" size="icon" className="size-8" onClick={() => handleViewBudget(comoBudget)}>
                                          <EyeIcon className="h-4 w-4" />
                                        </Button>
                                      </Tip>
                                      <Tip label={`PDF de la v${v.version}`}>
                                        <Button variant="outline" size="icon" className="size-8" onClick={() => handleDownloadPdf(comoBudget)}>
                                          <DocumentArrowDownIcon className="h-4 w-4" />
                                        </Button>
                                      </Tip>
                                    </ButtonGroup>
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                          </React.Fragment>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
          </div>

          {/* Vista móvil en cards */}
          <div className="md:hidden space-y-3 p-4">
            {filteredBudgets.length === 0 ? (
              <div className="text-center py-12">
                <CurrencyDollarIcon className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No hay presupuestos que coincidan con los filtros</p>
              </div>
            ) : (
              paginatedBudgets.map((budget) => {
                const ageDays = getBudgetAgeDays(budget.createdAt);
                const priority = getBudgetPriority(budget);
                const typeLabel = getBudgetTypeLabel(budget);
                return (
                <div key={budget.id} className="rounded-xl border bg-card/80 p-4 shadow-sm space-y-3">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs text-muted-foreground">Codigo</p>
                      <p className="text-base font-semibold">
                        {numeroSinVersion(budget)}
                        {(Number(budget.version) || 1) > 1 && <MarcaVersion version={budget.version} />}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {formatDate(budget.validUntil)}
                      </p>
                    </div>
                    <Badge 
                      variant={
                        budget.status === 'pending' ? 'default' :
                        budget.status === 'approved' ? 'default' :
                        budget.status === 'rejected' ? 'destructive' :
                        'secondary'
                      }
                      className={
                        budget.status === 'pending' ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100' :
                        budget.status === 'approved' ? 'bg-green-100 text-green-800 hover:bg-green-100' :
                        budget.status === 'expired' ? 'bg-gray-100 text-gray-800 hover:bg-gray-100' :
                        ''
                      }
                    >
                      {getStatusText(budget.status)}
                    </Badge>
                    {budget.status === 'expired' && (budget as any).recontactedAt && (
                      <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Recontactado</Badge>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-xs uppercase text-muted-foreground">Cliente</p>
                      <p className="font-medium">{budget.clientName}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase text-muted-foreground">Valor</p>
                      <p className="font-semibold">{formatCurrency(budget.totalAmount || 0)}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase text-muted-foreground">vehiculo</p>
                      <p className="text-muted-foreground">{budget.vehicleInfo || 'Sin vehículo'}</p>
                    </div>
                    <div>
                      <p className="text-xs uppercase text-muted-foreground">Tipo</p>
                      <Badge className={`mt-1 w-fit ${getBudgetTypeBadgeClass(typeLabel)}`}>{typeLabel}</Badge>
                    </div>
                    <div>
                      <p className="text-xs uppercase text-muted-foreground">Antiguedad</p>
                      <Badge className={`mt-1 w-fit ${getAgeBadgeClass(ageDays)}`}>{getBudgetAgeLabel(ageDays)}</Badge>
                    </div>
                    <div>
                      <p className="text-xs uppercase text-muted-foreground">Prioridad</p>
                      {priority === '-' ? (
                        <span className="text-muted-foreground">-</span>
                      ) : (
                        <Badge className={`mt-1 w-fit ${getPriorityBadgeClass(priority)}`}>{priority}</Badge>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col gap-2">
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full justify-center"
                        onClick={() => handleViewBudget(budget)}
                      >
                        <EyeIcon className="h-4 w-4 mr-2" />
                        Ver
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full justify-center"
                        onClick={() => handleShareLink(budget)}
                      >
                        <LinkIcon className="h-4 w-4 mr-2" />
                        Compartir
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full justify-center bg-primary/5 hover:bg-primary/10"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownloadPdf(budget);
                        }}
                      >
                        <DocumentArrowDownIcon className="h-4 w-4 md:mr-0 mr-2" />
                        <span className="md:hidden">PDF</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full justify-center"
                        onClick={() => handlePrintView(budget)}
                      >
                        <PrinterIcon className="h-4 w-4 mr-2" />
                        Imprimir
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <Toggle
                      variant="outline"
                      size="sm"
                      pressed={budget.whatsappSent || false}
                      onPressedChange={(pressed: boolean) => {
                        if (budget.whatsappSent) return;
                        if (pressed) {
                          handleSendWhatsApp(budget);
                        }
                      }}
                      disabled={budget.whatsappSent}
                      className="flex-1 min-w-[120px] justify-center data-[state=on]:bg-green-100 data-[state=on]:text-green-600 data-[state=on]:border-green-300"
                      aria-label={budget.whatsappSent ? "WhatsApp enviado" : "Enviar por WhatsApp"}
                    >
                      <svg className="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                      </svg>
                      WhatsApp
                    </Toggle>
                    <Toggle
                      variant="outline"
                      size="sm"
                      pressed={budget.emailSent || false}
                      onPressedChange={(pressed: boolean) => {
                        if (budget.emailSent) return;
                        if (pressed) {
                          handleSendEmail(budget);
                        }
                      }}
                      disabled={budget.emailSent}
                      className="flex-1 min-w-[120px] justify-center data-[state=on]:bg-green-100 data-[state=on]:text-green-600 data-[state=on]:border-green-300"
                      aria-label={budget.emailSent ? 'Email ya enviado' : 'Enviar por email'}
                    >
                      <EnvelopeIcon className="h-4 w-4 mr-1" />
                      Email
                    </Toggle>
                    {(budget.status === 'pending' || (budget.status === 'approved' && !(budget as any).hasRemito)) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleConfirmBudget(budget)}
                        className="col-span-2 sm:col-span-1 sm:flex-1 sm:min-w-[120px] justify-center text-green-600"
                      >
                        <CheckIcon className="h-4 w-4 mr-1" />
                        {budget.status === 'approved' ? 'Regenerar remito' : 'Aprobar'}
                      </Button>
                    )}
                    {esVersionable(budget) && (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={creandoVersion === budget.id}
                        onClick={() => handleNuevaVersion(budget)}
                        className="col-span-2 justify-center"
                      >
                        <DocumentDuplicateIcon className="h-4 w-4 mr-1" />
                        Nueva versión (v{(Number(budget.version) || 1) + 1})
                      </Button>
                    )}
                  </div>

                  {(budget.versionesAnteriores || []).length > 0 && (
                    <div className="border-t pt-2">
                      <button
                        type="button"
                        aria-expanded={versionesAbiertas.has(budget.id)}
                        onClick={() => toggleVersiones(budget.id)}
                        className="inline-flex items-center gap-1 rounded text-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                      >
                        {versionesAbiertas.has(budget.id) ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
                        {(budget.versionesAnteriores || []).length === 1
                          ? '1 versión anterior'
                          : `${(budget.versionesAnteriores || []).length} versiones anteriores`}
                      </button>
                      {versionesAbiertas.has(budget.id) && (
                        <ul className="mt-2 space-y-1.5">
                          {(budget.versionesAnteriores || []).map((v) => {
                            const comoBudget = { ...budget, ...versionComoBudget(v) };
                            return (
                              <li key={v.id} className="flex items-center justify-between gap-2 rounded-lg bg-muted/40 px-2 py-1.5 text-sm text-muted-foreground">
                                <div className="min-w-0">
                                  <div className="truncate font-medium">
                                    {numeroSinVersion(comoBudget)}
                                    <MarcaVersion version={v.version} apagada />
                                  </div>
                                  <div className="text-xs tabular-nums">
                                    {formatCurrency(v.total)}
                                    {v.reemplazadoAt ? ` · reemplazada el ${formatDate(v.reemplazadoAt)}` : ''}
                                  </div>
                                </div>
                                <div className="flex shrink-0 gap-1">
                                  <Button variant="outline" size="icon" className="size-8" aria-label={`Ver la v${v.version}`} onClick={() => handleViewBudget(comoBudget)}>
                                    <EyeIcon className="h-4 w-4" />
                                  </Button>
                                  <Button variant="outline" size="icon" className="size-8" aria-label={`PDF de la v${v.version}`} onClick={() => handleDownloadPdf(comoBudget)}>
                                    <DocumentArrowDownIcon className="h-4 w-4" />
                                  </Button>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              );})
            )}
          </div>
            </CardContent>
          </Card>

          {/* Paginación */}
          {!loading && filteredBudgets.length > 0 && (
            <div className="flex items-center justify-between px-2">
              <div className="hidden flex-1 text-muted-foreground text-sm md:block">
                Mostrando {startResult} de {totalResults} resultados
              </div>
              <div className="flex items-center space-x-6 lg:space-x-8">
                <div className="flex w-[100px] items-center justify-center font-medium text-sm">
                  pagina {currentPage} de {totalPages}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={goToFirstPage}
                  disabled={currentPage === 1}
                  className="shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium text-sm outline-none transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0 border bg-card shadow-xs hover:bg-accent hover:text-accent-foreground active:scale-[0.97] dark:border-input dark:bg-input/30 dark:hover:bg-input/50 size-9 hidden h-8 w-8 lg:flex"
                >
                  <span className="sr-only">Ir a la primera pagina</span>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                    <path d="m11 17-5-5 5-5"></path>
                    <path d="m18 17-5-5 5-5"></path>
                  </svg>
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={goToPreviousPage}
                  disabled={currentPage === 1}
                  className="inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium text-sm outline-none transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0 border bg-card shadow-xs hover:bg-accent hover:text-accent-foreground active:scale-[0.97] dark:border-input dark:bg-input/30 dark:hover:bg-input/50 size-9 h-8 w-8"
                >
                  <span className="sr-only">Ir a la pagina anterior</span>
                  <ChevronLeftIcon className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={goToNextPage}
                  disabled={currentPage === totalPages}
                  className="inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium text-sm outline-none transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0 border bg-card shadow-xs hover:bg-accent hover:text-accent-foreground active:scale-[0.97] dark:border-input dark:bg-input/30 dark:hover:bg-input/50 size-9 h-8 w-8"
                >
                  <span className="sr-only">Ir a la pagina siguiente</span>
                  <ChevronRightIcon className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={goToLastPage}
                  disabled={currentPage === totalPages}
                  className="shrink-0 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg font-medium text-sm outline-none transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0 border bg-card shadow-xs hover:bg-accent hover:text-accent-foreground active:scale-[0.97] dark:border-input dark:bg-input/30 dark:hover:bg-input/50 size-9 hidden h-8 w-8 lg:flex"
                >
                  <span className="sr-only">Ir a la última pagina</span>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                    <path d="m6 17 5-5-5-5"></path>
                    <path d="m13 17 5-5-5-5"></path>
                  </svg>
                </Button>
              </div>
            </div>
          )}

 {/* Vista calendario eliminada por requerimiento */}

 {/* Modal de creacion/edición de presupuesto */}
      <Dialog open={showCreateModal || showEditModal} onOpenChange={(open) => {
        if (!open) {
          setShowCreateModal(false);
          setShowEditModal(false);
          clearForm();
        }
      }}>
        <DialogContent className="!inset-0 !translate-x-0 !translate-y-0 !w-screen !h-screen !max-w-none !max-h-none rounded-none border-0 shadow-2xl p-0 overflow-hidden flex flex-col">
          <div className="p-4 sm:p-6 pb-0 sm:pb-0">
            <DialogHeader>
              <DialogTitle className="text-lg sm:text-xl font-semibold">
                {selectedBudget ? 'Editar Presupuesto' : 'Nuevo Presupuesto'}
              </DialogTitle>
            </DialogHeader>
          </div>
          <div className="px-4 sm:px-6 pb-4 sm:pb-6 pt-3 overflow-y-auto flex-1 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
            <form id="budget-form" onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              {/* Primera fila: Cliente y vehiculo */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="clientId" className="font-medium text-sm">Cliente *</Label>
                  <ClientCombobox
                    clients={clients || []}
                    selectedClient={selectedClient || null}
                    onSelect={(client) => {
                      if (client) {
                        setValue('clientId', client.id, { shouldValidate: true });
                        setValue('vehicleId', ''); // Limpiar vehiculo cuando cambia el cliente
                      } else {
                        setValue('clientId', '');
                        setValue('vehicleId', '');
                      }
                    }}
                    onCreateClient={(query) => handleCreateClientFromQuery(query)}
                    placeholder="Buscar cliente..."
                    getClientName={getClientName}
                  />
                  <ClientQuickPicks
                    onConsumidorFinal={() => {
                      // Asegurar que este en la lista local, si no el combo se ve vacio
                      setClients((prev: any[]) => prev.some((c: any) => c.id === 'consumidor-final')
                        ? prev
                        : [...prev, ({ id: 'consumidor-final', firstName: 'Consumidor', lastName: 'Final', type: 'individual', phone: '', email: '' } as any)]);
                      setValue('clientId', 'consumidor-final', { shouldValidate: true });
                      setValue('vehicleId', '');
                    }}
                    onPlateResolved={({ client, vehicle }) => {
                      // Sumarlo a la lista local para que el combo muestre el nombre
                      setClients((prev: any[]) => prev.some((c: any) => c.id === client.id)
                        ? prev
                        : [...prev, ({ id: client.id, firstName: client.name, lastName: '', phone: client.phone || '', isPlaceholder: client.placeholder ? 1 : 0 } as any)]);
                      setValue('clientId', client.id, { shouldValidate: true });
                      // El efecto de clientId re-pide los vehiculos; dejamos elegido el resuelto
                      if (vehicle?.id) {
                        setClientVehicles((prev: any[]) => prev.some((v: any) => v.id === vehicle.id) ? prev : [...prev, vehicle]);
                        setValue('vehicleId', vehicle.id, { shouldValidate: true });
                      } else {
                        setValue('vehicleId', '');
                      }
                    }}
                  />
                  <input
                    type="hidden"
                    {...register('clientId', { required: 'Cliente es requerido' })}
                  />
                  {errors.clientId && <p className="text-sm text-red-600 mt-1">{errors.clientId.message?.toString()}</p>}

                  {/* Consumidor Final: no hay cliente con teléfono, se puede cargar
                      uno acá para poder enviar el presupuesto por WhatsApp. */}
                  {selectedClientId === 'consumidor-final' && (
                    <div className="mt-2 space-y-1">
                      <Label htmlFor="contactPhone" className="text-xs text-muted-foreground">Teléfono (para enviar por WhatsApp)</Label>
                      <Input
                        id="contactPhone"
                        type="tel"
                        inputMode="tel"
                        placeholder="Ej. 3476409820"
                        {...register('contactPhone')}
                        className="h-9"
                      />
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vehicleId" className="font-medium text-sm">Vehículo</Label>
                  <Controller
                    name="vehicleId"
                    control={control}
                    render={({ field }) => (
                      <div className="flex items-stretch gap-2">
                        <div className="min-w-0 flex-1">
                          <Select
                            value={field.value || undefined}
                            onValueChange={(value) => {
                              if (value === '__create__') {
                                setVehicleModalOpen(true);
                                return;
                              }
                              field.onChange(value || '');
                            }}
                          >
                            <SelectTrigger className="h-auto px-4 py-3">
                              <SelectValue placeholder="Sin vehículo" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__create__">Crear vehículo...</SelectItem>
                              {clientVehicles.length === 0 ? (
                                <div className="px-2 py-1.5 text-sm text-muted-foreground">
                                  No hay vehículos disponibles
                                </div>
                              ) : (
                                clientVehicles.map((vehicle: any) => (
                                  <SelectItem key={vehicle.id} value={vehicle.id}>
                                    {getVehicleInfo(vehicle)}
                                  </SelectItem>
                                ))
                              )}
                            </SelectContent>
                          </Select>
                        </div>
                        {/* Marca y modelo al portapapeles, para pedir precio al proveedor */}
                        <VehicleCopyButton
                          vehicle={clientVehicles.find((v: any) => v.id === field.value)}
                          className="h-auto w-11 self-stretch"
                        />
                      </div>
                    )}
                  />
                </div>
              </div>

              {vehicleCreateDialog}

              {/* Segunda fila: Descripción y campos adicionales */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-2">
                    {/* Observaciones: el botón siempre dice lo mismo; si hay algo cargado
                        cambia de color y suma el tilde (Rodrigo, 05/10/2026). "Sin descripcion"
                        es el relleno que guarda el server cuando no se cargó nada. */}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowObservationsModal(true)}
                      className={`rounded-xl justify-start text-sm h-9 px-3 max-w-full ${hayObservaciones ? BOTON_CARGADO : BOTON_VACIO}`}
                    >
                      {hayObservaciones && <CheckIcon className="h-3.5 w-3.5 mr-1 shrink-0" />}
                      <span className="text-xs">Observaciones</span>
                    </Button>
                    {/* Descuento: aplicado cambia de color y muestra el nombre */}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setShowDiscountModal(true)}
                      className={`rounded-xl justify-start text-sm h-9 px-3 max-w-full ${appliedDiscount ? BOTON_CARGADO : BOTON_VACIO}`}
                    >
                      {appliedDiscount ? (
                        <span className="text-xs block max-w-[220px] truncate">{appliedDiscount.name} - {appliedDiscount.discount_type === 'percentage' ? `${appliedDiscount.discount_value}%` : `$${appliedDiscount.discount_value}`}</span>
                      ) : (
                        <span className="text-xs">Descuento</span>
                      )}
                    </Button>
                    {appliedDiscount && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedDiscount('');
                          setAppliedDiscount(null);
                        }}
                        className="rounded-xl text-sm h-9 px-3 bg-black dark:bg-white hover:bg-black/90 dark:hover:bg-white/90 text-white dark:text-black border-black dark:border-white"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  {/* Medios de pago (multiselect) */}
                  <div className="flex flex-col gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full justify-between rounded-md border-border bg-background text-foreground hover:bg-accent hover:text-accent-foreground"
                      onClick={() => {
                        const defaults = applyDefaultPaymentMethods(paymentMethods, selectedPaymentMethodsForBudget);
                        setSelectedPaymentMethodsForBudget(defaults);
                        setTempPaymentSelection(defaults);
                        setShowPaymentSelectorModal(true);
                      }}
                    >
                      <span className="text-sm">{paymentSelectionLabel()}</span>
                      <ChevronDownIcon className="h-4 w-4" />
                    </Button>

                    {/* Plazos de cheque: aparece solo si el medio cheque esta elegido */}
                    {selectedPaymentMethodsForBudget.some((c) => String(c).toLowerCase().includes('cheque')) && (
                      <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-2">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-medium">Cheque: plazos y recargos</p>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="h-7 rounded-lg text-xs"
                            onClick={() => setChequePlazos((prev) => [...prev, { plazo: '', pct: '', monto: '' }])}
                          >
                            Agregar plazo
                          </Button>
                        </div>
                        {chequePlazos.length === 0 ? (
                          <p className="text-xs text-muted-foreground">
                            Sin plazos cargados el cheque sale con su recargo general. Agregá filas tipo "30 días" con % de recargo o el precio final.
                          </p>
                        ) : (
                          <div className="space-y-1.5">
                            {chequePlazos.map((fila, i) => (
                              <div key={i} className="flex items-center gap-2">
                                <Input
                                  value={fila.plazo}
                                  onChange={(e) => setChequePlazos((prev) => prev.map((f, j) => (j === i ? { ...f, plazo: e.target.value } : f)))}
                                  placeholder="Plazo (ej: 30 días)"
                                  className="h-8 flex-1 min-w-0"
                                />
                                <Input
                                  type="number"
                                  value={fila.pct}
                                  onChange={(e) => setChequePlazos((prev) => prev.map((f, j) => (j === i ? { ...f, pct: e.target.value, monto: e.target.value !== '' ? '' : f.monto } : f)))}
                                  placeholder="% rec."
                                  className="h-8 w-24"
                                />
                                <span className="text-xs text-muted-foreground shrink-0">o</span>
                                <Input
                                  type="number"
                                  value={fila.monto}
                                  onChange={(e) => setChequePlazos((prev) => prev.map((f, j) => (j === i ? { ...f, monto: e.target.value, pct: e.target.value !== '' ? '' : f.pct } : f)))}
                                  placeholder="$ precio final"
                                  className="h-8 w-28"
                                />
                                <button
                                  type="button"
                                  onClick={() => setChequePlazos((prev) => prev.filter((_, j) => j !== i))}
                                  className="text-muted-foreground hover:text-destructive shrink-0"
                                >
                                  <XMarkIcon className="h-4 w-4" />
                                </button>
                              </div>
                            ))}
                            <p className="text-[11px] text-muted-foreground">
                              Cada plazo sale como una fila en el PDF. Cargá % de recargo O el precio final de ese plazo, no los dos.
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    <Dialog
                      open={showPaymentSelectorModal}
                      onOpenChange={(open) => {
                        if (open) {
                          const defaults = applyDefaultPaymentMethods(paymentMethods, selectedPaymentMethodsForBudget);
                          setTempPaymentSelection(defaults);
                          setPaymentMethodsSearch('');
                        }
                        setShowPaymentSelectorModal(open);
                      }}
                    >
                      <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                          <DialogTitle>Medios de pago</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-3">
                          <Input
                            placeholder="Buscar medio de pago..."
                            value={paymentMethodsSearch}
                            onChange={(e) => setPaymentMethodsSearch(e.target.value)}
                          />
                          <ScrollArea className="h-64 pr-2">
                            <div className="space-y-2">
                              {/* Si hay busqueda, mostrar todos filtrados sin agrupacion */}
                              {paymentMethodsSearch ? (
                                <>
                                  {paymentMethods
                                    .filter((method) => {
                                      const term = paymentMethodsSearch.toLowerCase();
                                      return (
                                        method.name.toLowerCase().includes(term) ||
                                        (method.code || '').toLowerCase().includes(term)
                                      );
                                    })
                                    .map((method) => {
                                      const checked = tempPaymentSelection.includes(method.code);
                                      return (
                                        <label
                                          key={method.code}
                                          className="flex items-center gap-3 rounded-md border border-border bg-background px-3 py-2 hover:bg-muted cursor-pointer"
                                        >
                                          <Checkbox
                                            checked={checked}
                                            onCheckedChange={(isChecked) => {
                                              setTempPaymentSelection((prev) =>
                                                isChecked
                                                  ? [...prev, method.code]
                                                  : prev.filter((c) => c !== method.code)
                                              );
                                            }}
                                          />
                                          <div className="flex flex-col">
                                            <span className="text-sm font-medium text-foreground">
                                              {formatPaymentMethodName(method.name)}
                                            </span>
                                            <span className="text-xs text-muted-foreground font-mono">
                                              {method.code}
                                            </span>
                                          </div>
                                        </label>
                                      );
                                    })}
                                  {paymentMethods.filter((method) => {
                                    const term = paymentMethodsSearch.toLowerCase();
                                    return (
                                      method.name.toLowerCase().includes(term) ||
                                      (method.code || '').toLowerCase().includes(term)
                                    );
                                  }).length === 0 && (
                                    <p className="text-sm text-muted-foreground px-1">Sin resultados.</p>
                                  )}
                                </>
                              ) : (
                                <>
                                  {/* Grupo Contado */}
                                  <div className="space-y-1">
                                    <label
                                      className="flex items-center gap-3 rounded-md border border-border bg-background px-3 py-2 hover:bg-muted cursor-pointer"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        const contadoMethods = paymentMethods.filter((m) =>
                                          contadoGroupCodes.includes(normalizeCode(m.code))
                                        );
                                        const allSelected = contadoMethods.every((m) =>
                                          tempPaymentSelection.includes(m.code)
                                        );

                                        if (allSelected) {
                                          // Deseleccionar todos los del grupo
                                          setTempPaymentSelection((prev) =>
                                            prev.filter((c) =>
                                              !contadoMethods.some((m) => m.code === c)
                                            )
                                          );
                                        } else {
                                          // Seleccionar todos los del grupo
                                          const codesToAdd = contadoMethods.map((m) => m.code);
                                          setTempPaymentSelection((prev) => {
                                            const newSelection = [...prev];
                                            codesToAdd.forEach((code) => {
                                              if (!newSelection.includes(code)) {
                                                newSelection.push(code);
                                              }
                                            });
                                            return newSelection;
                                          });
                                        }
                                      }}
                                    >
                                      <Checkbox
                                        checked={isContadoGroupComplete(tempPaymentSelection)}
                                        onCheckedChange={() => {}}
                                      />
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setContadoGroupExpanded(!contadoGroupExpanded);
                                        }}
                                        className="flex items-center gap-2 flex-1"
                                      >
                                        <ChevronDownIcon
                                          className={`h-4 w-4 transition-transform ${
                                            contadoGroupExpanded ? 'rotate-180' : ''
                                          }`}
                                        />
                                        <span className="text-sm font-medium text-foreground">
                                          Contado
                                        </span>
                                      </button>
                                    </label>

                                    {/* Metodos individuales del grupo contado (expandible) */}
                                    {contadoGroupExpanded && (
                                      <div className="ml-6 space-y-1">
                                        {paymentMethods
                                          .filter((m) => contadoGroupCodes.includes(normalizeCode(m.code)))
                                          .map((method) => {
                                            const checked = tempPaymentSelection.includes(method.code);
                                            return (
                                              <label
                                                key={method.code}
                                                className="flex items-center gap-3 rounded-md border border-border bg-background px-3 py-2 hover:bg-muted cursor-pointer"
                                              >
                                                <Checkbox
                                                  checked={checked}
                                                  onCheckedChange={(isChecked) => {
                                                    setTempPaymentSelection((prev) =>
                                                      isChecked
                                                        ? [...prev, method.code]
                                                        : prev.filter((c) => c !== method.code)
                                                    );
                                                  }}
                                                />
                                                <div className="flex flex-col">
                                                  <span className="text-sm font-medium text-foreground">
                                                    {formatPaymentMethodName(method.name)}
                                                  </span>
                                                  <span className="text-xs text-muted-foreground font-mono">
                                                    {method.code}
                                                  </span>
                                                </div>
                                              </label>
                                            );
                                          })}
                                      </div>
                                    )}
                                  </div>

                                  {/* Otros metodos de pago (no del grupo contado) */}
                                  {paymentMethods
                                    .filter((m) => !contadoGroupCodes.includes(normalizeCode(m.code)))
                                    .map((method) => {
                                      const checked = tempPaymentSelection.includes(method.code);
                                      return (
                                        <label
                                          key={method.code}
                                          className="flex items-center gap-3 rounded-md border border-border bg-background px-3 py-2 hover:bg-muted cursor-pointer"
                                        >
                                          <Checkbox
                                            checked={checked}
                                            onCheckedChange={(isChecked) => {
                                              setTempPaymentSelection((prev) =>
                                                isChecked
                                                  ? [...prev, method.code]
                                                  : prev.filter((c) => c !== method.code)
                                              );
                                            }}
                                          />
                                          <div className="flex flex-col">
                                            <span className="text-sm font-medium text-foreground">
                                              {formatPaymentMethodName(method.name)}
                                            </span>
                                            <span className="text-xs text-muted-foreground font-mono">
                                              {method.code}
                                            </span>
                                          </div>
                                        </label>
                                      );
                                    })}
                                </>
                              )}
                            </div>
                          </ScrollArea>
                        </div>
                        <DialogFooter className="flex justify-end gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => setShowPaymentSelectorModal(false)}
                          >
                            Cancelar
                          </Button>
                          <Button
                            type="button"
                            onClick={() => {
                              const applied = applyDefaultPaymentMethods(paymentMethods, tempPaymentSelection);
                              setSelectedPaymentMethodsForBudget(applied);
                              setShowPaymentSelectorModal(false);
                            }}
                          >
                            Guardar
                          </Button>
                        </DialogFooter>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
                <div className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="validUntil" className="font-medium text-sm">Válido hasta</Label>
                    <DatePicker
                      id="validUntil"
                      value={watch('validUntil')}
                      onChange={(date) => setValue('validUntil', date)}
                    />
                  </div>
                  {openTurnos.length > 0 && (
                    <div className="space-y-2">
                      <Label className="font-medium text-sm">Turno en taller (opcional)</Label>
                      <Select value={linkTurnoId || 'none'} onValueChange={(v) => setLinkTurnoId(v === 'none' ? '' : v)}>
                        <SelectTrigger className={linkTurnoId ? '' : 'text-muted-foreground'}>
                          <SelectValue placeholder="Sin enlazar" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Sin enlazar</SelectItem>
                          {openTurnos.map(t => (
                            <SelectItem key={t.id} value={t.id}>{t.code}{t.plate ? ` · ${t.plate}` : ''}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Si el cliente aprueba, los ítems se agregan a ese turno y el remito sale al terminar el servicio (no se genera uno aparte).
                      </p>
                    </div>
                  )}
                </div>
              </div>

 {/* Tercera fila: Items del presupuesto */}
 <div>
              {/* Opciones comparativas */}
              <div className="mb-4 flex items-center gap-3">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button type="button" onClick={()=>setShowOptionsModal(true)} className="px-3 py-2 rounded-xl border text-sm bg-card text-foreground border-border hover:bg-muted">
                      Agregar opciones comparativas
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="bg-gray-900 text-white border-gray-900">
                    <p className="text-sm">Los items base los agregas abajo. Las opciones se cargan en el mini-modal y no se suman entre si.</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              <Label className="block text-base font-semibold mb-4">
                Productos y Servicios
              </Label>

 {/* Selector de producto siempre visible */}
 <div className="grid grid-cols-12 gap-4 mb-6">
 <div className="col-span-12">
 <ProductCombobox
 products={products}
 selectedProduct={null}
 onSelect={(product) => {
 const newItem = {
 productId: product.id,
 name: product.name,
 description: product.name,
 sellingPrice: product.sellingPrice,
 unitPrice: product.sellingPrice,
 qty: 1,
 totalPrice: product.sellingPrice,
 type: product.isService ? 'servicio' : 'producto',
 comboItems: comboDesdeProducto(product)
 };
 setBudgetItems([...budgetItems, newItem]);
 }}
 onCreateRequested={(text) => {
 // Escrito a mano pero ya existe en el catálogo (sin mirar tildes ni
 // mayúsculas): cargar el artículo, no un ítem libre
 const delCatalogo: any = buscarEnCatalogo({ name: text }, products);
 const freeItem = delCatalogo ? {
 productId: delCatalogo.id,
 name: delCatalogo.name,
 description: delCatalogo.name,
 sellingPrice: delCatalogo.sellingPrice,
 unitPrice: delCatalogo.sellingPrice,
 qty: 1,
 totalPrice: delCatalogo.sellingPrice,
 type: delCatalogo.isService ? 'servicio' : 'producto',
 comboItems: comboDesdeProducto(delCatalogo)
 } : {
 productId: '',
 name: text,
 description: text,
 sellingPrice: 0,
 unitPrice: 0,
 qty: 1,
 totalPrice: 0,
 type: 'producto'
 };
 setBudgetItems([...budgetItems, freeItem]);
 }}
 createLabelOverride={(q) => (q ? `Agregar "${q}" (ítem libre)` : 'Escribí un ítem libre')}
 placeholder="Buscar producto o servicio..."
 />
 </div>
 </div>

              {/* Tabla de items agregados - Desktop */}
              {budgetItems.length > 0 && (
                <>
                {/* Desktop table */}
                <ScrollArea className="hidden md:block h-[400px] rounded-xl border shadow-sm border-border">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-muted sticky top-0 z-10">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Producto/Servicio</th>
                          <th className="px-3 py-3 text-center text-sm font-semibold text-foreground">Cant.</th>
                          <th className="px-3 py-3 text-right text-sm font-semibold text-foreground">Precio Unit.</th>
                          <th className="px-3 py-3 text-right text-sm font-semibold text-foreground">Total</th>
                          <th className="px-3 py-3 text-center text-sm font-semibold text-foreground">Acciones</th>
                        </tr>
                      </thead>
                      <tbody className="bg-card">
                      {budgetItems.map((item, index) => (
                        <tr
                          key={index}
                          className={`border-t border-border ${dragIndex === index ? 'opacity-40' : ''}`}
                          onDragOver={(e) => { e.preventDefault(); }}
                          onDrop={(e) => { e.preventDefault(); if (dragIndex !== null) moverItem(dragIndex, index); setDragIndex(null); }}
                        >
                          <td className="px-4 py-3 text-base text-foreground">
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                {/* Agarradera: arrastrar la fila para reordenar */}
                                <span
                                  draggable
                                  onDragStart={() => setDragIndex(index)}
                                  onDragEnd={() => setDragIndex(null)}
                                  title="Arrastrar para reordenar"
                                  className="shrink-0 cursor-grab select-none px-1 text-muted-foreground hover:text-foreground active:cursor-grabbing"
                                >
                                  ⠿
                                </span>
                                <input
                                  type="text"
                                  value={item.name || ''}
                                  onChange={(e) => {
                                    const newItems = [...budgetItems];
                                    newItems[index] = {
                                      ...newItems[index],
                                      name: e.target.value,
                                      description: e.target.value
                                    };
                                    setBudgetItems(newItems);
                                  }}
                                  className="flex-1 px-3 py-2 text-base border rounded-xl focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 bg-background border-border text-foreground"
                                  placeholder="Nombre del artículo"
                                />
                                {!item.productId && (
                                  <Tip label="Item sin producto vinculado - asignar precio manualmente">
                                    <span className="px-2 py-0.5 text-xs font-medium rounded bg-yellow-100 text-yellow-800 border border-yellow-300">
                                      Sin producto
                                    </span>
                                  </Tip>
                                )}
                              </div>
                              {item.discountApplied && (
                                <div className="text-xs text-green-600 font-medium">
                                  -{formatCurrency(item.discountAmount || 0)} ({item.discountApplied})
                                </div>
                              )}
                              {casillaDescuentoLibre(item, index)}
                              {Array.isArray((item as any).comboItems) && (item as any).comboItems.length > 0 && (
                                <div className="mt-1.5 space-y-0.5">
                                  {(item as any).comboItems.map((c: any, ci: number) => (
                                    <label key={ci} className="flex cursor-pointer items-center gap-2 text-[12.5px] text-muted-foreground">
                                      <input
                                        type="checkbox"
                                        checked={c.incluido !== false}
                                        onChange={() => {
                                          const newItems = [...budgetItems];
                                          newItems[index] = {
                                            ...newItems[index],
                                            comboItems: toggleComboItem((newItems[index] as any).comboItems, ci)
                                          };
                                          setBudgetItems(newItems);
                                        }}
                                        className="h-3.5 w-3.5 accent-foreground"
                                      />
                                      <span className={c.incluido !== false ? '' : 'line-through opacity-60'}>{c.label}</span>
                                    </label>
                                  ))}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-3">
                            <NumberInput
                              min={1}
                              value={item.qty || 1}
                              onChange={(v) => {
                                const newItems = [...budgetItems];
                                const newQty = v;
                                const basePrice = newItems[index].sellingPrice || 0;
                                newItems[index] = {
                                  ...newItems[index],
                                  qty: newQty,
                                  totalPrice: basePrice * newQty
                                };
                                setBudgetItems(newItems);
                              }}
                              className="w-20 px-2 py-2 text-center text-base border rounded-xl focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 bg-background border-border text-foreground"
                            />
                          </td>
                          <td className="px-3 py-3 text-right">
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.sellingPrice ? item.sellingPrice : ''}
                              onChange={(e) => {
                                const newItems = [...budgetItems];
                                const newPrice = parseFloat(e.target.value) || 0;
                                newItems[index] = {
                                  ...newItems[index],
                                  sellingPrice: newPrice,
                                  unitPrice: newPrice,
                                  totalPrice: newPrice * (newItems[index].qty || 1)
                                };
                                setBudgetItems(newItems);
                              }}
                              className="w-28 px-2 py-2 text-right text-base border rounded-xl focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 bg-background border-border text-foreground"
                              placeholder="0.00"
                            />
                          </td>
                          <td className="px-3 py-3 text-base text-right font-semibold text-foreground">
                            {formatCurrency((item.sellingPrice || 0) * (item.qty || 1))}
                          </td>
                          <td className="px-3 py-3 text-center">
                            <Tip label="Eliminar item">
                              <button
                                type="button"
                                onClick={() => {
                                  const newItems = budgetItems.filter((_, i) => i !== index);
                                  setBudgetItems(newItems);
                                }}
                                className="p-2 rounded-lg transition-colors text-destructive hover:bg-destructive/10"
                              >
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            </Tip>
                          </td>
                        </tr>
                      ))}
                      </tbody>
                    </table>
                  </div>
                </ScrollArea>

                {/* Vista móvil de items - Cards (sin scroll anidado: fluyen con la página) */}
                <div className="md:hidden space-y-3 rounded-xl border shadow-sm border-border p-3">
                  {budgetItems.map((item, index) => (
                    <div key={index} className="rounded-lg border border-border bg-card p-3 space-y-3">
                      <div className="flex items-start justify-between gap-2">
                        <input
                          type="text"
                          value={item.name || ''}
                          onChange={(e) => {
                            const newItems = [...budgetItems];
                            newItems[index] = {
                              ...newItems[index],
                              name: e.target.value,
                              description: e.target.value
                            };
                            setBudgetItems(newItems);
                          }}
                          className="flex-1 px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 bg-background border-border text-foreground"
                          placeholder="Nombre del artículo"
                        />
                        {/* En el celular el arrastre no va: se reordena con flechas */}
                        <div className="flex shrink-0 flex-col">
                          <button
                            type="button"
                            disabled={index === 0}
                            onClick={() => moverItem(index, index - 1)}
                            className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted disabled:opacity-25"
                          >
                            <ChevronUpIcon className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            disabled={index === budgetItems.length - 1}
                            onClick={() => moverItem(index, index + 1)}
                            className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted disabled:opacity-25"
                          >
                            <ChevronDownIcon className="h-4 w-4" />
                          </button>
                        </div>
                        <Tip label="Eliminar item">
                          <button
                            type="button"
                            onClick={() => {
                              const newItems = budgetItems.filter((_, i) => i !== index);
                              setBudgetItems(newItems);
                            }}
                            className="p-2 rounded-lg transition-colors text-destructive hover:bg-destructive/10 shrink-0"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </Tip>
                      </div>
                      {!item.productId && (
                        <span className="inline-block px-2 py-0.5 text-xs font-medium rounded bg-yellow-100 text-yellow-800 border border-yellow-300">
                          Sin producto
                        </span>
                      )}
                      {item.discountApplied && (
                        <div className="text-xs text-green-600 font-medium">
                          -{formatCurrency(item.discountAmount || 0)} ({item.discountApplied})
                        </div>
                      )}
                      {casillaDescuentoLibre(item, index)}
                      <div className="grid grid-cols-3 gap-2">
                        <div className="space-y-1">
                          <span className="text-xs text-muted-foreground">Cant.</span>
                          <NumberInput
                            min={1}
                            value={item.qty || 1}
                            onChange={(v) => {
                              const newItems = [...budgetItems];
                              const newQty = v;
                              const basePrice = newItems[index].sellingPrice || 0;
                              newItems[index] = {
                                ...newItems[index],
                                qty: newQty,
                                totalPrice: basePrice * newQty
                              };
                              setBudgetItems(newItems);
                            }}
                            className="w-full px-2 py-2 text-center text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 bg-background border-border text-foreground"
                          />
                        </div>
                        <div className="space-y-1">
                          <span className="text-xs text-muted-foreground">Precio Unit.</span>
                          <input
                            type="number"
                            inputMode="decimal"
                            min="0"
                            step="0.01"
                            value={item.sellingPrice ? item.sellingPrice : ''}
                            onChange={(e) => {
                              const newItems = [...budgetItems];
                              const newPrice = parseFloat(e.target.value) || 0;
                              newItems[index] = {
                                ...newItems[index],
                                sellingPrice: newPrice,
                                unitPrice: newPrice,
                                totalPrice: newPrice * (newItems[index].qty || 1)
                              };
                              setBudgetItems(newItems);
                            }}
                            className="w-full px-2 py-2 text-right text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 bg-background border-border text-foreground"
                            placeholder="0.00"
                          />
                        </div>
                        <div className="space-y-1">
                          <span className="text-xs text-muted-foreground">Total</span>
                          <div className="px-2 py-2 text-sm font-semibold text-foreground text-right">
                            {formatCurrency((item.sellingPrice || 0) * (item.qty || 1))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                </>
              )}

              {optionsItems.length > 0 && (
                <>
                {/* Opciones - tabla en desktop */}
                <div className="mt-4 hidden md:block rounded-xl border border-border">
                  <Table>
                    <TableHeader>
                      <TableRow className="hover:bg-muted/50 border-b">
                        <TableHead className="text-foreground h-10 px-3">Opcional</TableHead>
                        <TableHead className="text-foreground h-10 px-3 w-[110px] text-right">Cant.</TableHead>
                        <TableHead className="text-foreground h-10 px-3 w-[170px] text-right">Precio Unit.</TableHead>
                        <TableHead className="text-foreground h-10 px-3 w-[170px] text-right">Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {optionsItems.map((item, index) => (
                        <TableRow key={`${item.productId || item.name || 'opt'}-${index}`} className="border-b">
                          <TableCell className="px-3 py-2">
                            <div className="font-medium">{item.name || 'Item opcional'}</div>
                            {item.optionCode && (
                              <div className="text-xs text-muted-foreground">Opcion {item.optionCode}</div>
                            )}
                          </TableCell>
                          <TableCell className="px-3 py-2 text-right">{Number(item.qty || 1)}</TableCell>
                          <TableCell className="px-3 py-2 text-right">{formatCurrency(Number(item.sellingPrice || item.unitPrice || 0))}</TableCell>
                          <TableCell className="px-3 py-2 text-right font-semibold">{formatCurrency(Number(item.totalPrice || 0))}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {/* Opciones - tarjetas en mobile (cero scroll lateral) */}
                <div className="mt-4 md:hidden space-y-2 rounded-xl border border-border p-3">
                  <p className="text-xs font-semibold uppercase text-muted-foreground">Opciones (el cliente elige una)</p>
                  {optionsItems.map((item, index) => (
                    <div key={`${item.productId || item.name || 'opt'}-${index}`} className="rounded-lg border border-border bg-card p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{item.name || 'Item opcional'}</p>
                          {item.optionCode && <p className="text-xs text-muted-foreground">Opción {item.optionCode}</p>}
                        </div>
                        <p className="text-sm font-semibold shrink-0">{formatCurrency(Number(item.totalPrice || 0))}</p>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {Number(item.qty || 1)} × {formatCurrency(Number(item.sellingPrice || item.unitPrice || 0))}
                      </p>
                    </div>
                  ))}
                </div>
                </>
              )}
 </div>

 {/* Total (en mobile vive en el footer fijo, siempre a la vista) */}
 <div className="hidden sm:block p-6 rounded-xl border-2 bg-muted/50 border-border">
 <div className="flex justify-between items-center">
 <span className="text-xl font-bold text-foreground">Total del Presupuesto:</span>
 <span className="text-3xl font-bold text-primary">
 {formatCurrency(budgetItems.reduce((acc, item) => {
 const basePrice = (item.sellingPrice || item.unitPrice || 0) * (item.qty || item.quantity || 1);
 // Si tiene descuento aplicado, restar el monto del descuento
 if (item.discountApplied && item.discountAmount) {
 return acc + basePrice - item.discountAmount;
 }
 return acc + basePrice;
 }, 0))}
 </span>
 </div>
 </div>

 </form>
 </div>
          <div className="border-t border-border px-4 sm:px-6 py-3 sm:py-4 space-y-3">
            {/* Total compacto siempre visible en mobile */}
            <div className="flex sm:hidden items-center justify-between">
              <span className="text-sm font-semibold text-foreground">Total:</span>
              <span className="text-xl font-bold text-primary">
                {formatCurrency(budgetItems.reduce((acc, item) => {
                  const basePrice = (item.sellingPrice || item.unitPrice || 0) * (item.qty || item.quantity || 1);
                  if (item.discountApplied && item.discountAmount) {
                    return acc + basePrice - item.discountAmount;
                  }
                  return acc + basePrice;
                }, 0))}
              </span>
            </div>
            <DialogFooter className="gap-2 sm:gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowCreateModal(false);
                  setShowEditModal(false);
                  clearForm();
                }}
                className="rounded-xl h-11 w-full sm:w-auto bg-white hover:bg-gray-50 border-gray-300 text-gray-700"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                form="budget-form"
                className="rounded-xl h-11 w-full sm:w-auto bg-black dark:bg-white hover:bg-black/90 dark:hover:bg-white/90 text-white dark:text-black shadow-sm"
              >
                {selectedBudget ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

 {/* Mini-modal de Opciones comparativas */}
 <Dialog open={showOptionsModal} onOpenChange={setShowOptionsModal}>
   <DialogContent className="!inset-4 !translate-x-0 !translate-y-0 !w-[calc(100vw-2rem)] !h-[calc(100vh-2rem)] !max-w-none !max-h-none rounded-2xl border-0 shadow-2xl p-0 flex flex-col">
     <div className="p-6 pb-0">
       <DialogHeader>
         <DialogTitle className="text-xl font-semibold">Opciones Alternativas</DialogTitle>
         <p className="text-sm mt-1 text-muted-foreground">
           Las opciones no son acumulativas. El cliente elegirá solo una.
         </p>
       </DialogHeader>
     </div>

     {/* Body */}
     <div className="px-6 pb-6 overflow-y-auto flex-1">
 <div className={`mb-4 text-sm font-medium text-foreground`}>
 Opciones cargadas:
 </div>
 <div className="space-y-4">
 {optionsItems.length === 0 && (
 <div className={`text-center py-8 text-muted-foreground`}>
 <p className="text-sm">Aún no agregaste opciones alternativas.</p>
 <p className="text-xs mt-1">Usa el botón de abajo para agregar.</p>
 </div>
 )}
 {optionsItems.map((it, idx) => (
 <div
 key={`${it.productId}-${idx}`}
 className={`p-4 rounded-lg border ${
 'border-border bg-muted'
 }`}
 >
 {/* Header de la opcion */}
 <div className="flex items-center justify-between mb-3">
 <div className="flex items-center gap-2">
 <span className={`px-3 py-1 rounded-lg font-bold text-sm ${
 'bg-primary text-primary-foreground'
 }`}>
 OPCIÓN {it.optionCode || String.fromCharCode(65 + idx)}
 </span>
 </div>
 <Tip label="Eliminar opcion">
 <button type="button"
 onClick={() => {
 const filtered = optionsItems
   .filter((_, i) => i !== idx)
   .map((item, newIdx) => ({ ...item, optionCode: String.fromCharCode(65 + newIdx) }));
 setOptionsItems(filtered);
 }}
 className={`p-2 rounded-lg transition-colors ${
 'text-destructive hover:text-destructive hover:bg-destructive/10'
 }`}
 >
 <TrashIcon className="h-5 w-5" />
 </button>
 </Tip>
 </div>

 {/* Selector de producto con ProductCombobox */}
 <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
 <div className="md:col-span-5">
 <label className={`block text-xs font-medium mb-1 ${
 'text-muted-foreground'
 }`}>
 Producto/Servicio
 </label>
 <ProductCombobox
 products={products}
 selectedProduct={products.find((p) => String(p.id) === String(it.productId)) || null}
 onSelect={(product) => {
 const newList = [...optionsItems];
 newList[idx] = {
 ...newList[idx],
 productId: String(product.id),
 name: product.name,
 description: product.name,
 sellingPrice: product.sellingPrice,
 totalPrice: product.sellingPrice * (newList[idx].qty || 1),
 type: product.isService ? 'servicio' : 'producto',
 // Combo elegido como opcion: sus inclusiones viajan igual que en los items base
 comboItems: comboDesdeProducto(product),
 };
 setOptionsItems(newList);
 }}
 onCreateRequested={(text) => {
 setOptionsItems((prevList) => {
 const newList = [...prevList];
 const prev = newList[idx] || {};
 newList[idx] = {
 ...prev,
 productId: '',
 name: text,
 description: text,
 type: prev.type || 'producto',
 totalPrice: (prev.sellingPrice || 0) * (prev.qty || 1),
 };
 return newList;
 });
 }}
 createLabelOverride={(q) => (q ? `Usar "${q}" (sin crear)` : 'Escribí un ítem libre')}
 freeText={products.find((p) => String(p.id) === String(it.productId)) ? '' : (it.name || it.description || '')}
 placeholder="Buscar o escribir opcion..."
 />
 </div>

 <div className="md:col-span-2">
 <label className={`block text-xs font-medium mb-1 ${
 'text-muted-foreground'
 }`}>
 Cantidad
 </label>
 <NumberInput
 min={1}
 value={it.qty || 1}
 onChange={(v) => {
 const qty = v;
 const newList = [...optionsItems];
 newList[idx] = {
 ...newList[idx],
 qty,
 totalPrice: qty * (newList[idx].sellingPrice || 0),
 };
 setOptionsItems(newList);
 }}
 className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors ${
 'bg-background border-border text-foreground'
 }`}
 />
 </div>

 <div className="md:col-span-3">
 <label className={`block text-xs font-medium mb-1 ${
 'text-muted-foreground'
 }`}>
 Precio Unit.
 </label>
 <input
 type="number"
 min={0}
 step={1}
 // vacio en vez de 0: si no, al tipear queda "0345" pegado al cero inicial
 value={it.sellingPrice ? it.sellingPrice : ''}
 placeholder="0"
 onChange={(e) => {
 const price = parseFloat(e.target.value) || 0;
 const newList = [...optionsItems];
 newList[idx] = {
 ...newList[idx],
 sellingPrice: price,
 totalPrice: price * (newList[idx].qty || 1),
 };
 setOptionsItems(newList);
 }}
 className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors ${
 'bg-background border-border text-foreground'
 }`}
 />
 </div>

 <div className="md:col-span-2">
 <label className={`block text-xs font-medium mb-1 ${
 'text-muted-foreground'
 }`}>
 Total
 </label>
 <div className={`px-3 py-2 rounded-lg font-semibold text-sm ${
 'bg-card border border-border text-green-600 dark:text-green-400'
 }`}>
 {formatCurrency((it.sellingPrice || 0) * (it.qty || 1))}
 </div>
 {Array.isArray((it as any).comboItems) && (it as any).comboItems.length > 0 && (
 <div className="mt-1.5 space-y-0.5">
 {(it as any).comboItems.map((c: any, ci: number) => (
 <label key={ci} className="flex cursor-pointer items-center gap-2 text-[12.5px] text-muted-foreground">
 <input
 type="checkbox"
 checked={c.incluido !== false}
 onChange={() => {
 const newList = [...optionsItems];
 newList[idx] = { ...newList[idx], comboItems: toggleComboItem((newList[idx] as any).comboItems, ci) };
 setOptionsItems(newList);
 }}
 className="h-3.5 w-3.5 accent-foreground"
 />
 <span className={c.incluido !== false ? '' : 'line-through opacity-60'}>{c.label}</span>
 </label>
 ))}
 </div>
 )}
 {it.discountApplied && (
 <div className="text-xs text-green-600 font-medium mt-1">
 -{formatCurrency(it.discountAmount || 0)} ({it.discountApplied})
 </div>
 )}
 </div>
 </div>
 </div>
 ))}
 <button
 type="button"
 onClick={() => {
 // Usar el siguiente código disponible después del mayor existente
 const usedCodes = optionsItems.map(it => it.optionCode || '').filter(Boolean);
 const maxCode = usedCodes.length > 0
   ? String.fromCharCode(Math.max(...usedCodes.map((c: string) => c.charCodeAt(0))))
   : String.fromCharCode(64); // 64 = 'A' - 1
 const nextCode = String.fromCharCode(maxCode.charCodeAt(0) + 1);
 setOptionsItems([
 ...optionsItems,
 {
 productId: '',
 name: '',
 description: '',
 sellingPrice: 0,
 qty: 1,
 totalPrice: 0,
 optionCode: nextCode,
 type: 'producto',
 },
 ]);
 }}
 className={`w-full mt-4 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-dashed font-medium transition-colors ${
 'border-border text-muted-foreground hover:border-primary hover:text-primary hover:bg-muted'
 }`}
 >
 <PlusIcon className="h-5 w-5" />
 Agregar nueva opcion
 </button>
     </div>
     </div>

     {/* Footer */}
     <DialogFooter className="gap-3 pt-6 px-6 pb-6">
       <Button
         type="button"
         variant="outline"
         onClick={() => setShowOptionsModal(false)}
         className="rounded-xl bg-white hover:bg-gray-50 border-gray-300 text-gray-700"
       >
         Cancelar
       </Button>
       <Button
         type="button"
         onClick={() => {
           setShowOptionsModal(false);
           toast.success(`${optionsItems.length} opcion(es) agregada(s)`);
         }}
         className="px-4 py-2 bg-black dark:bg-white text-white dark:text-black rounded-xl font-medium hover:bg-black/90 dark:hover:bg-white/90 transition-colors inline-flex items-center gap-2"
       >
         <CheckIcon className="h-5 w-5" />
         Guardar opciones
       </Button>
     </DialogFooter>
   </DialogContent>
 </Dialog>

      {/* Modal de descuentos */}
      <Dialog open={showDiscountModal} onOpenChange={setShowDiscountModal}>
        <DialogContent className="max-w-2xl rounded-3xl border-0 shadow-2xl p-0 overflow-hidden">
          <div className="p-6 pb-0">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold">Seleccionar Descuento</DialogTitle>
            </DialogHeader>
          </div>
          <div className="px-6 pb-6 overflow-y-auto max-h-[calc(90vh-140px)]">
            <div className="space-y-4">
              {/* Cupones de descuento */}
              <div className="space-y-2">
                <Label className="font-medium text-sm">Cupones de descuento</Label>
                <CouponInput
                  onCouponApplied={handleCouponApplied}
                  onCouponRemoved={handleCouponRemoved}
                  appliedCoupon={appliedCoupon}
                  totalAmount={budgetItems.reduce((sum, item) => sum + (item.totalPrice || 0), 0)}
                  items={budgetItems.map(item => ({
                    type: item.type || 'service',
                    totalPrice: item.totalPrice || 0
                  }))}
                  clientId={selectedClientId}
                />
              </div>

              {/* Descuentos personalizados */}
              <div className="space-y-2">
                <Label className="font-medium text-sm">Descuentos personalizados</Label>
                {enabledDiscounts.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <p className="text-sm">No hay descuentos disponibles</p>
                  </div>
                ) : (
                  <>
                    <div className="md:hidden">
                      <Select
                        value={selectedDiscount ? String(selectedDiscount) : undefined}
                        onValueChange={(value) => {
                          handleDiscountChange(value);
                          setShowDiscountModal(false);
                        }}
                      >
                        <SelectTrigger className="w-full rounded-xl h-11">
                          <SelectValue placeholder="Seleccionar descuento" />
                        </SelectTrigger>
                        <SelectContent>
                          {enabledDiscounts.map((discount) => (
                            <SelectItem key={discount.id} value={String(discount.id)}>
                              <div className="flex flex-col">
                                <span className="font-medium text-foreground">{discount.name}</span>
                                <span className="text-xs text-muted-foreground">
                                  {discount.discount_type === 'percentage'
                                    ? `${discount.discount_value}%`
                                    : `$${discount.discount_value}`}
                                </span>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="hidden md:block space-y-2">
                      {enabledDiscounts.map((discount) => (
                        <button
                          key={discount.id}
                          type="button"
                          onClick={() => {
                            handleDiscountChange(String(discount.id));
                            setShowDiscountModal(false);
                          }}
                          className={`w-full text-left p-4 rounded-xl border transition-colors ${
                            selectedDiscount === discount.id || String(selectedDiscount) === String(discount.id)
                              ? 'border-primary bg-primary/10'
                              : 'border-border hover:bg-muted'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-medium text-foreground">{discount.name}</div>
                              <div className="text-sm text-muted-foreground mt-1">{discount.description}</div>
                              {discount.min_amount > 0 && (
                                <div className="text-xs text-muted-foreground mt-1">
                                  Monto mínimo: ${discount.min_amount}
                                </div>
                              )}
                            </div>
                            <div className="text-lg font-semibold text-primary">
                              {discount.discount_type === 'percentage' 
                                ? `${discount.discount_value}%` 
                                : `$${discount.discount_value}`}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          <DialogFooter className="gap-3 pt-6 px-6 pb-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowDiscountModal(false);
              }}
              className="rounded-xl bg-white hover:bg-gray-50 border-gray-300 text-gray-700"
            >
              Cancelar
            </Button>
            {appliedDiscount && (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setSelectedDiscount('');
                  setAppliedDiscount(null);
                  setShowDiscountModal(false);
                }}
                className="rounded-xl"
              >
                Quitar descuento
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de observaciones */}
      <Dialog open={showObservationsModal} onOpenChange={setShowObservationsModal}>
        <DialogContent className="max-w-2xl rounded-3xl border-0 shadow-2xl p-0 overflow-hidden">
          <div className="p-6 pb-0">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold">Observaciones</DialogTitle>
            </DialogHeader>
          </div>
          <div className="px-6 pb-6 overflow-y-auto max-h-[calc(90vh-140px)]">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="font-medium text-sm">Observaciones adicionales (opcional)</Label>
                <Textarea
                  id="description"
                  {...register('description')}
                  rows={8}
                  className="rounded-lg"
                  placeholder="Escribe las observaciones aquí..."
                />
              </div>
            </div>
          </div>
          <DialogFooter className="gap-3 pt-6 px-6 pb-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowObservationsModal(false);
              }}
              className="rounded-xl bg-white hover:bg-gray-50 border-gray-300 text-gray-700"
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={() => {
                setShowObservationsModal(false);
              }}
              className="px-4 py-2 bg-black dark:bg-white text-white dark:text-black rounded-xl font-medium hover:bg-black/90 dark:hover:bg-white/90 transition-colors inline-flex items-center gap-2"
            >
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: ¿Crear turno asociado al presupuesto? */}
      <Dialog
        open={showCreateTurnoModal}
        onOpenChange={(open) => {
          if (!open) {
            setShowCreateTurnoModal(false);
            setBudgetForTurno(null);
            setModalTurnos([]);
            setModalTurnoId('');
          }
        }}
      >
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>¿Cómo seguimos con {budgetForTurno?.budgetNumber || 'el presupuesto'}?</DialogTitle>
            <DialogDescription>
              El cliente aprobó. Los productos y servicios se cargan solos donde elijas.
            </DialogDescription>
          </DialogHeader>
          {/* La OT del presupuesto ya cerró pero el auto sigue en el taller
              (listo para retirar): ofrecer reabrirla y sumarle los items */}
          {(budgetForTurno as any)?.linkedAppointmentId && (budgetForTurno as any)?.linkedAppointmentStatus === 'ready_for_pickup' && (
            <div className="space-y-2 rounded-lg border border-primary bg-primary/5 p-3">
              <Label className="text-sm font-medium">
                La {(budgetForTurno as any).linkedAppointmentCode || 'OT'} de este trabajo ya se cerró, pero el auto sigue en el taller
              </Label>
              <Button
                type="button"
                className="w-full"
                onClick={async () => {
                  const b = budgetForTurno;
                  if (!b) return;
                  const aptId = (b as any).linkedAppointmentId;
                  const aptCode = (b as any).linkedAppointmentCode || 'OT';
                  try {
                    await axios.post(`/api/reception/${aptId}/reopen`, {
                      reason: `Se aprobó el presupuesto ${b.budgetNumber || ''}: trabajo adicional`.trim()
                    });
                    await axios.post(`/api/budgets/${b.id}/link-appointment`, { appointmentId: aptId });
                    toast.success(`${aptCode} reabierta: los items se agregaron y salen en su remito al terminar`);
                    setShowCreateTurnoModal(false);
                    setBudgetForTurno(null);
                    setModalTurnos([]);
                    setModalTurnoId('');
                    await fetchBudgets();
                  } catch (e: any) {
                    toast.error(e?.response?.data?.error || 'No se pudo reabrir la OT');
                  }
                }}
              >
                Reabrir la {(budgetForTurno as any).linkedAppointmentCode || 'OT'} y agregarle los items
              </Button>
              <p className="text-xs text-muted-foreground">
                La OT vuelve a producción con el motivo anotado en el parte, y estos items entran en su mismo remito.
              </p>
            </div>
          )}
          {modalTurnos.length > 0 && (
            <div className={`space-y-2 rounded-lg border p-3 ${modalTurnos.some(t => t.mismoVehiculo) ? 'border-primary bg-primary/5' : ''}`}>
              <Label className="text-sm font-medium">
                {modalTurnos.some(t => t.mismoVehiculo)
                  ? 'Este vehículo está en el taller ahora'
                  : 'El cliente ya tiene un vehículo en el taller'}
              </Label>
              <div className="flex items-center gap-2">
                <Select value={modalTurnoId || 'none'} onValueChange={(v) => setModalTurnoId(v === 'none' ? '' : v)}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Elegir turno..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Elegir turno...</SelectItem>
                    {modalTurnos.map(t => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.code}{t.plate ? ` · ${t.plate}` : ''}{t.mismoVehiculo ? ' (este auto)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  disabled={!modalTurnoId}
                  onClick={async () => {
                    const b = budgetForTurno;
                    if (!b || !modalTurnoId) return;
                    try {
                      const resp = await axios.post(`/api/budgets/${b.id}/link-appointment`, { appointmentId: modalTurnoId });
                      toast.success(
                        resp.data?.sumadoAOtro
                          ? `Se sumó a la ${resp.data?.appointmentCode || 'OT'}, que ya tenía otro presupuesto: sale todo en el mismo remito`
                          : `Ítems agregados al turno ${resp.data?.appointmentCode || ''}: el remito sale al terminar el servicio`
                      );
                      setShowCreateTurnoModal(false);
                      setBudgetForTurno(null);
                      setModalTurnos([]);
                      setModalTurnoId('');
                      await fetchBudgets();
                    } catch (e: any) {
                      toast.error(e?.response?.data?.error || 'No se pudo enlazar el turno');
                    }
                  }}
                >
                  Enlazar
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Los ítems se agregan a ese turno y salen en el mismo remito al terminar el servicio.
                Sirve aunque el turno ya tenga otro presupuesto.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={async () => {
                const b = budgetForTurno;
                setShowCreateTurnoModal(false);
                setBudgetForTurno(null);
                if (!b) return;
                // No crea turno: entonces se genera el remito ahora mismo.
                try {
                  await axios.post(`/api/remitos/from-budget/${b.id}`);
                  toast.success('Remito borrador creado');
                  await fetchBudgets();
                } catch (error: any) {
                  toast.error(error.response?.data?.error || 'Error al crear el remito');
                }
              }}
            >
              Ya está hecho: generar remito
            </Button>
            <Button
              type="button"
              onClick={() => {
                if (!budgetForTurno) return;
                const params = new URLSearchParams();
                if (budgetForTurno.clientId) params.set('clientId', String(budgetForTurno.clientId));
                if ((budgetForTurno as any).vehicleId) params.set('vehicleId', String((budgetForTurno as any).vehicleId));
                params.set('budgetId', String(budgetForTurno.id));
                setShowCreateTurnoModal(false);
                setBudgetForTurno(null);
                navigate(`/appointments-enhanced/new?${params.toString()}`);
              }}
            >
              Programar un turno nuevo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

 {/* Modal de visualización de presupuesto */}
 {showViewModal && viewBudget && viewClient && (
 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
 <div className={`rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto ${
 'bg-card'
 }`}>
 <div className={`flex justify-between items-center p-6 border-b ${
 'border-border'
 }`}>
 <h3 className={`text-xl font-semibold ${
 'text-foreground'
 }`}>
 Presupuesto {numeroSinVersion(viewBudget)}
 {((Number(viewBudget.version) || 1) > 1 || viewBudget.status === 'superseded') && (
   <MarcaVersion version={viewBudget.version} apagada={viewBudget.status === 'superseded'} />
 )}
 </h3>
 <div className="flex items-center gap-2">
 {esVersionable(viewBudget) && (
   <Button
     variant="outline"
     size="sm"
     disabled={creandoVersion === viewBudget.id}
     onClick={() => handleNuevaVersion(viewBudget)}
     className="flex items-center gap-2"
   >
     <DocumentDuplicateIcon className="h-4 w-4" />
     Nueva versión
   </Button>
 )}
 {viewBudget.status !== 'superseded' && (
 <Button
 variant="outline"
 size="sm"
 onClick={() => {
 handleEdit(viewBudget);
 setShowViewModal(false);
 setViewBudget(null);
 setViewClient(null);
 setViewVehicle(null);
 }}
 className="flex items-center gap-2"
 >
 <PencilIcon className="h-4 w-4" />
 Editar
 </Button>
 )}
 <button type="button"
 onClick={() => {
 setShowViewModal(false);
 setViewBudget(null);
 setViewClient(null);
 setViewVehicle(null);
 }}
 className={` ${
 'text-muted-foreground hover:text-foreground'
 }`}
 >
 <XMarkIcon className="h-6 w-6" />
 </button>
 </div>
 </div>
 
 <div className="p-6 space-y-6">
 {viewBudget.status === 'superseded' && (
   <div className="rounded-lg border bg-muted/40 px-4 py-3 text-sm text-muted-foreground">
     Esta versión fue reemplazada por una más nueva
     {viewBudget.reemplazadoAt ? ` el ${formatDate(viewBudget.reemplazadoAt)}` : ''}.
     Queda como se le pasó al cliente, sin cambios.
   </div>
 )}
 {/* Información General */}
 <div>
 <h4 className={`text-lg font-semibold mb-4 ${
 'text-foreground'
 }`}>Información General</h4>
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <div>
 <span className={`text-sm font-medium ${
 'text-muted-foreground'
 }`}>Cliente</span>
 <p className={` ${
 'text-foreground'
 }`}>{getClientName(viewClient)}</p>
 </div>
 <div>
 <span className={`text-sm font-medium ${
 'text-muted-foreground'
 }`}>vehiculo</span>
 <p className={` ${
 'text-foreground'
 }`}>
 {viewVehicle ? getVehicleInfo(viewVehicle) : 'Sin vehículo'}
 </p>
 </div>
 <div>
 <span className={`text-sm font-medium ${
 'text-muted-foreground'
 }`}>Estado</span>
 <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
 viewBudget.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
 viewBudget.status === 'approved' ? 'bg-green-100 text-green-800' :
 viewBudget.status === 'rejected' ? 'bg-red-100 text-red-800' :
 viewBudget.status === 'expired' ? 'bg-gray-100 text-gray-800' :
 'bg-blue-100 text-blue-800'
 }`}>
 {getStatusText(viewBudget.status)}
 </span>
 </div>
 <div>
 <span className={`text-sm font-medium ${
 'text-muted-foreground'
 }`}>Válido hasta</span>
 <p className={` ${
 'text-foreground'
 }`}>{formatDate(viewBudget.validUntil)}</p>
 </div>
 </div>
 </div>

 {/* Detalles */}
 <div>
 <h4 className={`text-lg font-semibold mb-4 ${
 'text-foreground'
 }`}>Detalles</h4>
 <div className="space-y-3">
 {viewBudget.description && (
 <div>
 <span className={`text-sm font-medium ${
 'text-muted-foreground'
 }`}>Observaciones</span>
 <p className={` ${
 'text-foreground'
 }`}>{viewBudget.description}</p>
 </div>
 )}
 <div>
 <span className={`text-sm font-medium ${
 'text-muted-foreground'
 }`}>Creado</span>
 <p className={` ${
 'text-foreground'
 }`}>{formatDate(viewBudget.createdAt)}</p>
 </div>
 {viewBudget.paymentMethod && (
 <div>
 <span className={`text-sm font-medium ${
 'text-muted-foreground'
 }`}>Medio de pago seleccionado</span>
 <p className={` ${
 'text-foreground'
 }`}>
 <span className="font-semibold text-blue-600">{
 viewBudget.paymentMethod === 'tc6' ? 'TC 6' :
 viewBudget.paymentMethod === 'tc3' ? 'TC 3P' :
 (viewBudget.paymentMethod === 'contado' || viewBudget.paymentMethod === 'cash' || viewBudget.paymentMethod === 'efectivo') ? 'Contado' :
 viewBudget.paymentMethod
 }</span>
 </p>
 </div>
 )}
 </div>
 </div>



 {/* Productos y Servicios (Base + Opcion elegida si aplica) */}
 <div>
 <h4 className={`text-lg font-semibold mb-4 ${
 'text-foreground'
 }`}>Productos y Servicios</h4>
 <div className="space-y-2">

 {(() => {
 const items: any[] = Array.isArray((viewBudget as any).items) ? (viewBudget as any).items : [];
 const hasOptions = items.some((it:any) => !!it.optionCode);
 let toShow: any[] = items;
 if (hasOptions && (viewBudget as any).selectedOption) {
 const base = items.filter((it:any)=>!it.optionCode);
 const chosen = items.filter((it:any)=>String(it.optionCode)===String((viewBudget as any).selectedOption));
 toShow = [...base, ...chosen];
 }
 return toShow && toShow.length > 0 ? (
 toShow.map((item: any, index: number) => {
 // Lo que el cliente sacó al aprobar: se muestra igual, tachado, para
 // saber qué se le ofreció y no quiso.
 const rechazado = !!item.rejected;
 return (
 <div key={index} className="flex justify-between items-center py-2 border-b border-border">
 <div className={rechazado ? 'text-muted-foreground line-through' : 'text-foreground'}>
 <span>{item.description || item.name}</span>
 <span className="ml-2 text-muted-foreground">x{item.qty || item.quantity || 1}</span>
 </div>
 <div className="flex items-center gap-2">
 {rechazado && (
   <span className="rounded-full border border-destructive/40 px-2 py-0.5 text-[11px] font-medium text-destructive no-underline">
     Rechazado
   </span>
 )}
 <span className={`font-medium ${rechazado ? 'text-muted-foreground line-through' : 'text-foreground'}`}>
 {formatCurrency((item.totalPrice) ?? ((item.sellingPrice || item.unitPrice || 0) * (item.qty || item.quantity || 1)))}
 </span>
 </div>
 </div>
 );
 })
 ) : (
 <p className={` ${
 'text-muted-foreground'
 }`}>No hay items en este presupuesto</p>
 );
 })()}

 </div>
 {(Array.isArray((viewBudget as any).items) && (viewBudget as any).items.some((i:any)=>!!i.optionCode)) && (
 <div className="mt-4 flex justify-end">
 <button type="button"
 className="px-3 py-2 text-sm bg-gray-200 hover:bg-gray-300 text-gray-800 rounded"
 onClick={()=>setShowDiscardedOptionsModal(true)}
 >
 Ver opciones descartadas
 </button>
 </div>
 )}
 </div>

 {/* Resumen de descuento aplicado (si lo hay) */}
 {(() => {
   const items: any[] = Array.isArray((viewBudget as any).items) ? (viewBudget as any).items : [];
   const hasOptions = items.some((it:any) => !!it.optionCode);
   const itemsShown = (hasOptions && (viewBudget as any).selectedOption)
     ? [...items.filter((it:any)=>!it.optionCode), ...items.filter((it:any)=>String(it.optionCode)===String((viewBudget as any).selectedOption))]
     : items;
   const lineTotal = (it:any) => Number(it.totalPrice) || (Number(it.sellingPrice || it.unitPrice || 0) * Number(it.qty || it.quantity || 1));
   // Lo rechazado se muestra pero no cuenta para los totales
   const itemsCobrados = itemsShown.filter((it:any) => !it.rejected);
   const subtotal = itemsCobrados.reduce((s:number, it:any) => s + lineTotal(it), 0);
   let discountTotal = itemsCobrados.reduce((s:number, it:any) => s + (Number(it.discountAmount) || 0), 0);
   let discountName = itemsCobrados.find((it:any)=>it.discountApplied)?.discountApplied || '';
   if (discountTotal === 0 && (viewBudget as any).appliedDiscount) {
     try {
       const d = typeof (viewBudget as any).appliedDiscount === 'string' ? JSON.parse((viewBudget as any).appliedDiscount) : (viewBudget as any).appliedDiscount;
       if (d) {
         discountName = d.name || discountName;
         if (d.discount_type === 'percentage') { discountTotal = Math.round(subtotal * Number(d.discount_value || 0) / 100); if (d.max_discount) discountTotal = Math.min(discountTotal, Number(d.max_discount)); }
         else if (d.discount_type === 'fixed') { discountTotal = Number(d.discount_value || 0); }
       }
     } catch {}
   }
   if (discountTotal <= 0) return null;
   if (!discountName) discountName = 'Descuento';
   return (
   <div className="mt-4 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
     <div className="flex justify-between text-sm">
       <span className="text-muted-foreground">Subtotal</span>
       <span className="text-foreground">{formatCurrency(subtotal)}</span>
     </div>
     <div className="flex justify-between text-sm mt-1">
       <span className="text-green-600 dark:text-green-400 font-medium">Descuento ({discountName})</span>
       <span className="text-green-600 dark:text-green-400 font-medium">-{formatCurrency(discountTotal)}</span>
     </div>
   </div>
   );
 })()}

 {/* Medio de Pago y Total - Solo si hay uno elegido */}
 {viewBudget.paymentMethod && (
 <div className="mt-6">
 <div className={`p-4 rounded-lg ${
 'bg-muted border border-border'
 }`}>
 <div className="flex justify-between items-center">
 <div>
 <span className={`font-medium ${
 'text-foreground'
 }`}>
 Medio de pago: <strong className="text-blue-500">{
 viewBudget.paymentMethod === 'tc6' ? 'TC 6' :
 viewBudget.paymentMethod === 'tc3' ? 'TC 3P' :
 (viewBudget.paymentMethod === 'contado' || viewBudget.paymentMethod === 'cash' || viewBudget.paymentMethod === 'efectivo') ? 'Contado' :
 viewBudget.paymentMethod
 }</strong>
 </span>
 <div className={`text-sm mt-1 ${
 'text-muted-foreground'
 }`}>
 {viewBudget.paymentMethod === 'Contado' ? 'Precio sin recargo' :
 viewBudget.paymentMethod === 'TC 3P' ? 'Precio con recargo del 15%' :
 viewBudget.paymentMethod === 'TC 6' ? 'Precio con recargo del 25%' :
 'Precio final'}
 </div>
 </div>
 <div className="text-right">
 <div className={`text-2xl font-bold ${
 viewBudget.paymentMethod === 'Contado' ? 'text-green-600' :
 viewBudget.paymentMethod === 'TC 3P' ? 'text-blue-600' :
 viewBudget.paymentMethod === 'TC 6' ? 'text-purple-600' :
 'text-gray-600'
 } `}>
 {(() => {
 // Calcular el total real sumando todos los items mostrados (base + opcion elegida)
 const items: any[] = Array.isArray((viewBudget as any).items) ? (viewBudget as any).items : [];
 const hasOptions = items.some((it:any) => !!it.optionCode);
 
 let totalAmount = 0;
 if (hasOptions && (viewBudget as any).selectedOption) {
 // Si hay opciones y una elegida, sumar base + opcion elegida
 const base = items.filter((it:any)=>!it.optionCode);
 const chosen = items.filter((it:any)=>String(it.optionCode)===String((viewBudget as any).selectedOption));
 const allItems = [...base, ...chosen];
 
 totalAmount = allItems.reduce((sum: number, item: any) => {
 const qty = Number(item.qty || item.quantity || 1);
 const unitPrice = Number(item.sellingPrice || item.unitPrice || 0);
 const totalPrice = Number(item.totalPrice) || (qty * unitPrice);
 return sum + totalPrice;
 }, 0);
 } else {
 // Si no hay opciones, sumar todos los items
 totalAmount = items.reduce((sum: number, item: any) => {
 const qty = Number(item.qty || item.quantity || 1);
 const unitPrice = Number(item.sellingPrice || item.unitPrice || 0);
 const totalPrice = Number(item.totalPrice) || (qty * unitPrice);
 return sum + totalPrice;
 }, 0);
 }
 
 // Restar el descuento aplicado (por item o, si no hay, del appliedDiscount del presupuesto)
 const itemsForDisc = (hasOptions && (viewBudget as any).selectedOption)
 ? [...items.filter((it:any)=>!it.optionCode), ...items.filter((it:any)=>String(it.optionCode)===String((viewBudget as any).selectedOption))]
 : items;
 let discountTotal = itemsForDisc.reduce((s:number, it:any) => s + (Number(it.discountAmount) || 0), 0);
 if (discountTotal === 0 && (viewBudget as any).appliedDiscount) {
 try {
 const d = typeof (viewBudget as any).appliedDiscount === 'string' ? JSON.parse((viewBudget as any).appliedDiscount) : (viewBudget as any).appliedDiscount;
 if (d && d.discount_type === 'percentage') { discountTotal = Math.round(totalAmount * Number(d.discount_value || 0) / 100); if (d.max_discount) discountTotal = Math.min(discountTotal, Number(d.max_discount)); }
 else if (d && d.discount_type === 'fixed') { discountTotal = Number(d.discount_value || 0); }
 } catch {}
 }
 const baseAfterDisc = Math.max(0, totalAmount - discountTotal);
 // Aplicar recargo según el medio de pago
 const paymentMethod = viewBudget.paymentMethod?.toLowerCase();
 if (paymentMethod === 'tc3' || paymentMethod === 'tc 3p') return formatCurrency(Math.round(baseAfterDisc * 1.15));
 if (paymentMethod === 'tc6' || paymentMethod === 'tc 6p') return formatCurrency(Math.round(baseAfterDisc * 1.25));
 return formatCurrency(baseAfterDisc);
 })()}
 </div>

 </div>
 </div>
 </div>
 </div>
 )}
 </div>
 </div>
 </div>
 )}

 {/* Modal aprobar: elegir opcion */}
 {showApproveModal && approveTarget && (() => {
   const itemTotal = (r: any) => (r.totalPrice) ?? ((r.sellingPrice || r.unitPrice || 0) * (r.qty || r.quantity || 1));
   const isService = (i: any) => { const t = String(i?.type || '').toLowerCase(); return t === 'service' || t === 'servicio'; };
   const isProduct = (i: any) => { const t = String(i?.type || '').toLowerCase(); return t && t !== 'service' && t !== 'servicio'; };
   const baseRows = approveItems.filter((i: any) => !i.optionCode);
   // Presupuesto sin opciones alternativas: el modal sirve igual para elegir
   // qué renglones se hacen (destildar = rechazado)
   const sinOpciones = approveOptionsCodes.length === 0;
   // Lo que el cliente saca al aprobar no se borra: queda rechazado, pero no
   // suma al total ni pasa a la orden de trabajo.
   const baseTomados = baseRows.filter((r: any) => !rejectedItems.has(String(r.id)));
   const baseSum = baseTomados.reduce((s: number, r: any) => s + itemTotal(r), 0);
   const optionRows = approveItems.filter((i: any) => String(i.optionCode) === selectedApproveOption);
   const optionSum = optionRows.reduce((s: number, r: any) => s + itemTotal(r), 0);
   // Aplicar descuento del presupuesto si existe (respetando applies_to)
   const budgetDiscount = (() => {
     const raw = (approveTarget as any)?.appliedDiscount;
     if (!raw) return null;
     try { return typeof raw === 'string' ? JSON.parse(raw) : raw; } catch { return null; }
   })();
   let discountAmount = 0;
   if (budgetDiscount && (selectedApproveOption || sinOpciones)) {
     const allShownItems = [...baseTomados, ...optionRows];
     const appliesTo = String(budgetDiscount.applies_to || 'all').toLowerCase();
     const targetType = String(budgetDiscount.target_type || '').toLowerCase() ||
       (appliesTo === 'services' ? 'services'
         : (appliesTo === 'products' || appliesTo === 'specific_product' || appliesTo === 'specific_brand' || appliesTo === 'filter') ? 'products'
         : 'both');
     const elegibles = serviciosElegibles(budgetDiscount);
     // Ítems libres solo si se tildó su casilla; los tildados entran siempre
     const { automaticos, tildados } = separarPorDecision(allShownItems);
     const tildadoComoServicio = (r: any) => targetType === 'services' || (targetType === 'both' && isService(r));
     const serviceItems = (targetType === 'services' || targetType === 'both')
       ? [...automaticos.filter((r: any) => entraEnDescuentoDeServicios(r, elegibles, isService, serviciosExcluidos(budgetDiscount))), ...tildados.filter(tildadoComoServicio)]
       : [];
     const productItems = (targetType === 'products' || targetType === 'both')
       ? [...automaticos.filter((r: any) => isProduct(r) && !serviceItems.includes(r)), ...tildados.filter((r: any) => !serviceItems.includes(r))]
       : [];
     const productBase = productItems.reduce((s, r) => s + itemTotal(r), 0);
     const serviceBase = serviceItems.reduce((s, r) => s + itemTotal(r), 0);
     if (productBase + serviceBase > 0) {
       if (budgetDiscount.discount_type === 'percentage') {
         const dv = Number(budgetDiscount.discount_value || 0);
         const dvSrv = budgetDiscount.discount_value_services != null ? Number(budgetDiscount.discount_value_services) : null;
         if (targetType === 'both' && dvSrv != null) {
           discountAmount = (productBase * dv / 100) + (serviceBase * dvSrv / 100);
         } else {
           discountAmount = ((productBase + serviceBase) * dv) / 100;
         }
         if (budgetDiscount.max_discount && discountAmount > Number(budgetDiscount.max_discount)) {
           discountAmount = Number(budgetDiscount.max_discount);
         }
       } else if (budgetDiscount.discount_type === 'fixed') {
         discountAmount = Number(budgetDiscount.discount_value || 0);
       }
     }
   }
   const grandTotal = Math.round(baseSum + optionSum - discountAmount);
   return (
 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
 <div className="rounded-lg shadow-xl w-full max-w-3xl bg-card border border-border flex flex-col max-h-[90vh]">
 <div className="flex items-center justify-between p-4 border-b border-border">
 <h3 className="text-foreground text-lg font-semibold">{sinOpciones ? 'Aprobar presupuesto' : 'Elegir opcion'}</h3>
 <button type="button" onClick={()=>setShowApproveModal(false)} className="text-muted-foreground hover:text-foreground transition-colors">✕</button>
 </div>
 <div className="p-4 space-y-4 overflow-auto flex-1">
 <p className="text-muted-foreground text-sm">{sinOpciones
   ? 'Confirmá qué renglones se hacen. Lo que destildes queda registrado como rechazado.'
   : 'Este presupuesto tiene opciones alternativas. Seleccioná una opcion para aprobar.'}</p>
 {baseRows.length > 0 && (
   <div className="rounded-md border border-border overflow-hidden">
     <div className="flex items-center justify-between px-3 py-2 bg-muted/50">
       <span className="font-medium text-foreground">{sinOpciones ? 'Items del presupuesto' : 'Base (incluido en todas las opciones)'}</span>
       <span className="text-sm text-muted-foreground">Subtotal: <strong className="text-foreground">{formatCurrency(baseSum)}</strong></span>
     </div>
     <div className="px-3 pt-2 text-xs text-muted-foreground">
       Destildá lo que el cliente no quiera hacer: queda registrado como rechazado, no se cobra ni pasa al taller.
     </div>
     <table className="w-full text-sm">
       <thead>
         <tr className="bg-muted/30 text-muted-foreground border-t border-border">
           <th className="text-left px-3 py-2 font-medium w-10"></th>
           <th className="text-left px-3 py-2 font-medium">Descripción</th>
           <th className="text-center px-3 py-2 font-medium w-16">Cant</th>
           <th className="text-right px-3 py-2 font-medium w-28">Precio Unit.</th>
           <th className="text-right px-3 py-2 font-medium w-28">Total</th>
         </tr>
       </thead>
       <tbody>
         {baseRows.map((r: any, idx: number) => {
           const id = String(r.id ?? idx);
           const rechazado = rejectedItems.has(id);
           return (
           <tr key={id} className={`border-t border-border/50 ${rechazado ? 'text-muted-foreground line-through opacity-60' : 'text-foreground'}`}>
             <td className="px-3 py-2 no-underline">
               <Tip label={rechazado ? 'Volver a incluirlo' : 'Sacarlo: queda rechazado'}>
                 <input
                   type="checkbox"
                   checked={!rechazado}
                   onChange={() => setRejectedItems(prev => {
                     const next = new Set(prev);
                     if (next.has(id)) next.delete(id); else next.add(id);
                     return next;
                   })}
                   className="accent-primary h-4 w-4 cursor-pointer align-middle"
                 />
               </Tip>
             </td>
             <td className="px-3 py-2">{r.description || r.name}</td>
             <td className="px-3 py-2 text-center">{r.qty || r.quantity || 1}</td>
             <td className="px-3 py-2 text-right">{formatCurrency(r.sellingPrice || r.unitPrice || 0)}</td>
             <td className="px-3 py-2 text-right">{formatCurrency((r.sellingPrice || r.unitPrice || 0) * (r.qty || r.quantity || 1))}</td>
           </tr>
           );
         })}
       </tbody>
     </table>
   </div>
 )}
 <div className="space-y-3">
 {approveOptionsCodes.map(code => {
 const rows = approveItems.filter(i=>String(i.optionCode)===code);
 const sum = rows.reduce((s: number, r: any) => s + ((r.totalPrice) ?? ((r.sellingPrice || r.unitPrice || 0) * (r.qty || r.quantity || 1))), 0);
 const isSelected = selectedApproveOption === code;
 return (
 <div
   key={code}
   className={`rounded-md border overflow-hidden transition-all cursor-pointer ${isSelected ? 'border-primary ring-2 ring-primary/30 bg-primary/5' : 'border-border hover:border-primary/50'}`}
   onClick={() => setSelectedApproveOption(code)}
 >
 <div className="flex items-center justify-between px-3 py-2 bg-muted/50">
 <label className="flex items-center gap-2 cursor-pointer text-foreground font-medium">
 <input type="radio" name="approveOption" value={code} checked={isSelected} onChange={()=>setSelectedApproveOption(code)} className="accent-primary" />
 Opcion {code}
 </label>
 <div className="flex items-center gap-3">
 <span className="text-xs text-muted-foreground">No acumulativa</span>
 <span className="text-sm text-foreground">Subtotal: <strong>{formatCurrency(sum)}</strong></span>
 </div>
 </div>
 <table className="w-full text-sm">
 <thead>
 <tr className="bg-muted/30 text-muted-foreground border-t border-border">
 <th className="text-left px-3 py-2 font-medium">Descripción</th>
 <th className="text-center px-3 py-2 font-medium w-16">Cant</th>
 <th className="text-right px-3 py-2 font-medium w-28">Precio Unit.</th>
 <th className="text-right px-3 py-2 font-medium w-28">Total</th>
 </tr>
 </thead>
 <tbody>
 {rows.map((r, idx) => (
 <tr key={idx} className="text-foreground border-t border-border/50">
 <td className="px-3 py-2">{r.description || r.name}</td>
 <td className="px-3 py-2 text-center">{r.qty || r.quantity || 1}</td>
 <td className="px-3 py-2 text-right">{formatCurrency(r.sellingPrice || r.unitPrice || 0)}</td>
 <td className="px-3 py-2 text-right">{formatCurrency((r.sellingPrice || r.unitPrice || 0) * (r.qty || r.quantity || 1))}</td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 );
 })}
 </div>
 {(selectedApproveOption || sinOpciones) && (
   <div className="rounded-md border border-primary/40 bg-primary/5 px-4 py-3 space-y-2">
     {discountAmount > 0 && (
       <div className="flex items-center justify-between text-sm">
         <span className="text-muted-foreground">{sinOpciones ? 'Subtotal' : `Subtotal (Base + Opcion ${selectedApproveOption})`}</span>
         <span className="text-foreground">{formatCurrency(baseSum + optionSum)}</span>
       </div>
     )}
     {discountAmount > 0 && (
       <div className="flex items-center justify-between text-sm text-green-600">
         <span>Descuento: {budgetDiscount?.name || ''} {budgetDiscount?.discount_type === 'percentage' ? `(${budgetDiscount?.discount_value}%)` : ''}</span>
         <span>-{formatCurrency(discountAmount)}</span>
       </div>
     )}
     <div className="flex items-center justify-between">
       <span className="text-foreground font-medium">Total final{discountAmount > 0 ? ' con descuento' : (sinOpciones ? '' : ` (Base + Opcion ${selectedApproveOption})`)}</span>
       <span className="text-lg font-bold text-foreground">{formatCurrency(grandTotal)}</span>
     </div>
   </div>
 )}
 </div>
 <div className="p-4 flex justify-end gap-2 border-t border-border">
 <button type="button" onClick={()=>setShowApproveModal(false)} className="px-4 py-2 rounded-md bg-muted text-foreground hover:bg-muted/80 transition-colors">Cancelar</button>
 <button type="button"
 disabled={!sinOpciones && !selectedApproveOption}
 onClick={async ()=>{
 try {
 if (!approveTarget) return;
 // Calcular total contado final = Base + Opcion elegida - Descuento del presupuesto (respetando applies_to)
 const _itemTotal = (r:any) => (r.totalPrice) ?? ((r.sellingPrice||r.unitPrice||0) * (r.qty||r.quantity||1));
 const _isService = (i:any) => { const t = String(i?.type || '').toLowerCase(); return t === 'service' || t === 'servicio'; };
 const _isProduct = (i:any) => { const t = String(i?.type || '').toLowerCase(); return t && t !== 'service' && t !== 'servicio'; };
 const baseRowsVal = approveItems.filter((i:any)=>!i.optionCode && !rejectedItems.has(String(i.id)));
 const optRowsVal = approveItems.filter((i:any)=>String(i.optionCode)===selectedApproveOption);
 const baseSumVal = baseRowsVal.reduce((s:number,i:any)=> s + _itemTotal(i), 0);
 const optSum = optRowsVal.reduce((s:number,i:any)=> s + _itemTotal(i), 0);
 // Aplicar descuento del presupuesto
 let discAmt = 0;
 const rawDisc = (approveTarget as any)?.appliedDiscount;
 const disc = rawDisc ? (typeof rawDisc === 'string' ? (()=>{try{return JSON.parse(rawDisc);}catch{return null;}})() : rawDisc) : null;
 if (disc) {
   const shown = [...baseRowsVal, ...optRowsVal];
   const appliesTo = String(disc.applies_to || 'all').toLowerCase();
   const targetType = String(disc.target_type || '').toLowerCase() ||
     (appliesTo === 'services' ? 'services'
       : (appliesTo === 'products' || appliesTo === 'specific_product' || appliesTo === 'specific_brand' || appliesTo === 'filter') ? 'products'
       : 'both');
   const elegibles = serviciosElegibles(disc);
   // Ítems libres solo si se tildó su casilla; los tildados entran siempre
   const { automaticos, tildados } = separarPorDecision(shown);
   const tildadoComoServicio = (r: any) => targetType === 'services' || (targetType === 'both' && _isService(r));
   const srvRows = (targetType === 'services' || targetType === 'both')
     ? [...automaticos.filter((r:any) => entraEnDescuentoDeServicios(r, elegibles, _isService, serviciosExcluidos(disc))), ...tildados.filter(tildadoComoServicio)]
     : [];
   const prodBase = (targetType === 'products' || targetType === 'both')
     ? [...automaticos.filter((r:any) => _isProduct(r) && !srvRows.includes(r)), ...tildados.filter((r:any) => !srvRows.includes(r))].reduce((s,r)=>s+_itemTotal(r),0)
     : 0;
   const srvBase = srvRows.reduce((s,r)=>s+_itemTotal(r),0);
   if (prodBase + srvBase > 0) {
     if (disc.discount_type === 'percentage') {
       const dv = Number(disc.discount_value || 0);
       const dvSrv = disc.discount_value_services != null ? Number(disc.discount_value_services) : null;
       if (targetType === 'both' && dvSrv != null) {
         discAmt = (prodBase * dv / 100) + (srvBase * dvSrv / 100);
       } else {
         discAmt = ((prodBase + srvBase) * dv) / 100;
       }
       if (disc.max_discount && discAmt > Number(disc.max_discount)) discAmt = Number(disc.max_discount);
     } else if (disc.discount_type === 'fixed') {
       discAmt = Number(disc.discount_value || 0);
     }
   }
 }
 const finalCash = Math.round(baseSumVal + optSum - discAmt);

 // Los renglones sacados viajan marcados: el backend los guarda igual
 // para que el presupuesto muestre después qué se rechazó.
 const itemsConRechazo = approveItems.map((i:any) => ({ ...i, rejected: rejectedItems.has(String(i.id)) }));
 await axios.put(`/api/budgets/${approveTarget.id}`, {
   ...approveTarget,
   items: itemsConRechazo,
   status: 'approved',
   ...(selectedApproveOption ? { selectedOption: selectedApproveOption } : {}),
   totalAmount: finalCash
 });
 await new Promise(r=>setTimeout(r,300));
 // El remito NO se crea aca: lo decide el modal de turno (igual que el camino normal).
 const cuantosRechazados = rejectedItems.size;
 const conOpcion = selectedApproveOption ? `, opcion ${selectedApproveOption}` : '';
 toast.success(cuantosRechazados
   ? `Presupuesto aprobado${conOpcion}. ${cuantosRechazados} ${cuantosRechazados === 1 ? 'renglón rechazado' : 'renglones rechazados'}.`
   : `Presupuesto aprobado${conOpcion}.`);
 const targetForAppt = approveTarget;
 setShowApproveModal(false);
 setApproveTarget(null);
 await fetchBudgets();
 // Ya enlazado a un turno abierto: items al turno, remito al terminar (backend)
 if ((targetForAppt as any)?.linkedAppointmentActive) {
   toast.success(`Los ítems van al turno ${(targetForAppt as any).linkedAppointmentCode || ''}; el remito sale al terminar el servicio`);
 } else if (targetForAppt) {
   // Abrir modal preguntando si quiere crear un turno asociado
   setBudgetForTurno(targetForAppt);
   setShowCreateTurnoModal(true);
 }
 } catch (err:any) {
 toast.error(err.response?.data?.error || 'Error al aprobar con opcion');
 }
 }}
 className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
 >Confirmar</button>
 </div>
 </div>
 </div>
   );
 })()}

 {/* Vista imprimible */}
 {showPrintView && printBudget && printClient && (
 <BudgetPrintView
 budget={printBudget}
 client={printClient}
 vehicle={printVehicle || undefined}
 onClose={() => {
 setShowPrintView(false);
 setPrintBudget(null);
 setPrintClient(null);
 setPrintVehicle(null);
 }}
 />
 )}

 {/* Modal: Opciones descartadas */}
 {showDiscardedOptionsModal && viewBudget && (
 <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
 <div className={`rounded-lg shadow-xl w-full max-w-3xl bg-card border border-border`}>
 <div className="flex items-center justify-between p-4 border-b">
 <h3 className={`text-foreground text-lg font-semibold`}>Opciones descartadas</h3>
 <button type="button" onClick={()=>setShowDiscardedOptionsModal(false)} className={`text-muted-foreground hover:text-foreground`}>✕</button>
 </div>
 <div className="p-4">
 {(() => {
 // Capturar variables del scope exterior
 // Theme handled by shadcn/ui
 const currency = formatCurrency;

 const items: any[] = Array.isArray((viewBudget as any).items) ? (viewBudget as any).items : [];
 const selected = String((viewBudget as any).selectedOption || '').trim();
 const codes = Array.from(new Set(items.filter(i=>!!i.optionCode).map(i=>String(i.optionCode))));
 const discardedCodes = selected ? codes.filter(c => String(c) !== selected) : codes;
 if (discardedCodes.length === 0) {
 return <p className="text-muted-foreground">No hay opciones descartadas.</p>;
 }
 return (
 <div className="space-y-4 max-h-[60vh] overflow-auto">
 {discardedCodes.map(code => {
 const rows = items.filter(i=>String(i.optionCode)===String(code));
 const sum = rows.reduce((s, r) => s + ((r.totalPrice) ?? ((r.sellingPrice||r.unitPrice||0) * (r.qty||r.quantity||1))), 0);
 return (
 <div key={code} className="rounded border border-border">
 <div className="flex items-center justify-between px-3 py-2 bg-muted text-foreground">
 <div>Opcion {code}</div>
 <div className="text-sm">Subtotal opcion: <strong>{currency(sum)}</strong></div>
 </div>
 <table className="w-full text-sm">
 <thead>
 <tr className="bg-muted text-muted-foreground">
 <th className="text-left px-3 py-2">Descripción</th>
 <th className="text-left px-3 py-2">Cant</th>
 <th className="text-left px-3 py-2">Precio Unit.</th>
 <th className="text-left px-3 py-2">Total</th>
 </tr>
 </thead>
 <tbody>
 {rows.map((r, idx) => (
 <tr key={idx} className="text-foreground">
 <td className="px-3 py-1">{r.description || r.name}</td>
 <td className="px-3 py-1">{r.qty || r.quantity || 1}</td>
 <td className="px-3 py-1">{currency(r.sellingPrice || r.unitPrice || 0)}</td>
 <td className="px-3 py-1">{currency((r.totalPrice) ?? ((r.sellingPrice || r.unitPrice || 0) * (r.qty || r.quantity || 1)))}</td>
 </tr>
 ))}
 </tbody>
 </table>
 </div>
 );
 })}
 </div>
 );
 })()}
 </div>
 </div>
 </div>
 )}

      {/* Modal sugerencia de turno post-aprobación */}
      <Dialog open={showTurnoSuggestion} onOpenChange={setShowTurnoSuggestion}>
        <DialogContent className="max-w-md rounded-2xl border-0 shadow-2xl p-0">
          <div className="p-6">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold">¿Agendamos un turno?</DialogTitle>
            </DialogHeader>
            <p className="text-muted-foreground mt-2 text-sm">
              El presupuesto fue aprobado y el remito generado. ¿Querés agendar el turno para que el cliente traiga el vehículo?
            </p>
            <div className="flex gap-3 mt-6 justify-end">
              <Button variant="outline" className="rounded-xl" onClick={() => setShowTurnoSuggestion(false)}>
                Ahora no
              </Button>
              <Button
                className="rounded-xl bg-black dark:bg-white hover:bg-black/90 dark:hover:bg-white/90 text-white dark:text-black"
                onClick={() => {
                  setShowTurnoSuggestion(false);
                  const params = new URLSearchParams();
                  if (approvedBudgetForTurno?.clientId) params.set('clientId', approvedBudgetForTurno.clientId);
                  if ((approvedBudgetForTurno as any)?.vehicleId) params.set('vehicleId', (approvedBudgetForTurno as any).vehicleId);
                  if (approvedBudgetForTurno?.id) params.set('budgetId', approvedBudgetForTurno.id);
                  navigate('/appointments-enhanced/new?' + params.toString());
                }}
              >
                Agendar turno
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

 </div>
 );
};

export default Budgets;





